
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { motion, AnimatePresence } from "framer-motion";
import { FiPlusCircle, FiSend, FiSun, FiMoon, FiSunrise, FiSunset, FiClock, FiCalendar, FiMapPin } from "react-icons/fi";

import {
  addLocation,
  createSchedulesBatch,
  deleteSchedule,
  fetchAllUsers,
  fetchLocations,
  fetchSchedulesInRange,
  publishSchedules,
  updateSchedule,
  remindAssignedShifts,
} from '../api';
import { useAuth } from '../context/AuthContext';
import { CalendarIcon,PlusIcon, PencilIcon ,ClockIcon, BookOpenIcon, PauseCircleIcon,XMarkIcon } from '@heroicons/react/24/outline';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const NOTIFICATION_EVENT = 'new-notification';

const generateTimeOptions = () => {
  const times = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const hourStr = hour.toString().padStart(2, '0');
      const minuteStr = minute.toString().padStart(2, '0');
      times.push(`${hourStr}:${minuteStr}`);
    }
  }
  return times;
};

const AssignmentModal = ({ isOpen, onClose, assignment, dayDate, availableLocations, defaultLocation, availableCareWorkers, onSave, onDelete, schedules, pendingSchedules, isAdmin }) => {
  const [formData, setFormData] = useState({
    id: assignment?.id || uuidv4(),
    start: assignment?.start ? assignment.start : "12:00",
end: assignment?.end ? assignment.end : "20:00",
    description: assignment?.description || '',
    careWorkers: assignment?.careWorkers || [],
    location: defaultLocation || availableLocations?.[0]?.name || 'Time Off',
    date: assignment?.date || dayDate || '',
    breakDuration: assignment?.breakDuration 
  ? parseInt(assignment.breakDuration) 
  : assignment?.break 
    ? parseInt(assignment.break) 
    : 0

  });
  const [error, setError] = useState('');
  const [isCareWorkerDropdownOpen, setIsCareWorkerDropdownOpen] = useState(false);
  const [careWorkerSearch, setCareWorkerSearch] = useState('');
const [locationSearch, setLocationSearch] = useState('');
const [isLocationSelectFocused, setIsLocationSelectFocused] = useState(false);
const [isStartTimeDropdownOpen, setIsStartTimeDropdownOpen] = useState(false); // Controls visibility of start time dropdown
  const [isEndTimeDropdownOpen, setIsEndTimeDropdownOpen] = useState(false); // Controls visibility of end time dropdown
  const [startTimeSearch, setStartTimeSearch] = useState(''); // Stores search input for start time filtering
  const [endTimeSearch, setEndTimeSearch] = useState(''); // Stores search input for end time
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [highlightedEndIndex, setHighlightedEndIndex] =useState(-1);

  const dropdownRef = useRef(null);
  const locationDropdownRef = useRef(null); 
  const locationSearchInputRef = useRef(null); 
  const careWorkerSearchInputRef = useRef(null);
  const startTimeDropdownRef = useRef(null); // Tracks start time dropdown for click-outside handling
  const endTimeDropdownRef = useRef(null); // Tracks end time dropdown for click-outside handling
  const startTimeSearchInputRef = useRef(null); // Ref for auto-focusing start time search input
  const endTimeSearchInputRef = useRef(null); // Ref for auto-focusing end time search input

  const timeOptions = useMemo(() => generateTimeOptions(), []);

  useEffect(() => {
    if (isOpen) {
      if (assignment) {
        setFormData({
          id: assignment.id || uuidv4(),
          start: assignment?.start ? assignment.start : "12:00",
    end: assignment?.end ? assignment.end : "20:00",
          description: assignment.description || '',
          careWorkers: assignment.careWorkers ||[],
          location:
  typeof assignment.location === 'object' && assignment.location.name
    ? assignment.location.name
    : /^[a-f\d]{24}$/i.test(assignment.location)
    ? availableLocations?.find(loc => loc._id === assignment.location)?.name
    : assignment.location || 'Time Off',

          date: assignment.date || '',
          breakDuration: assignment?.breakDuration
    ? parseInt(assignment.breakDuration)
    : assignment?.break
      ? parseInt(assignment.break)
      : 0,
      });
      } else {
        setFormData({
          id: uuidv4(),
          start: "12:00",
          end: "20:00",
          description: '',
          careWorkers: [],
          location: defaultLocation || availableLocations?.[0]?._id || 'Time Off',
          date: dayDate,
          breakDuration: 0,
        });
      }
      setError('');
      setIsCareWorkerDropdownOpen(false);
      setCareWorkerSearch(''); // Reset search when modal opens
      setLocationSearch(''); // Reset location search
    }
  }, [isOpen, assignment, defaultLocation, availableLocations]);
   useEffect(() => {
        const handleClickOutsideLocation = (event) => {
            if (locationDropdownRef.current && !locationDropdownRef.current.contains(event.target)) {
                setIsLocationSelectFocused(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutsideLocation);
        return () => {
            document.removeEventListener('mousedown', handleClickOutsideLocation);
        };
    }, []);

    useEffect(() => {
    const handleClickOutsideStartTime = (event) => {
      if (startTimeDropdownRef.current && !startTimeDropdownRef.current.contains(event.target)) {
        setIsStartTimeDropdownOpen(false); // Close start time dropdown when clicking outside
      }
    };
    document.addEventListener('mousedown', handleClickOutsideStartTime);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideStartTime);
    };
  }, []);

  useEffect(() => {
    const handleClickOutsideEndTime = (event) => {
      if (endTimeDropdownRef.current && !endTimeDropdownRef.current.contains(event.target)) {
        setIsEndTimeDropdownOpen(false); // Close end time dropdown when clicking outside
      }
    };
    document.addEventListener('mousedown', handleClickOutsideEndTime);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideEndTime);
    };
  }, []);

    useEffect(() => {
    if (isCareWorkerDropdownOpen && careWorkerSearchInputRef.current) {
      careWorkerSearchInputRef.current.focus();
    }
  }, [isCareWorkerDropdownOpen]);

    useEffect(() => {
    if (isLocationSelectFocused && locationSearchInputRef.current) {
      locationSearchInputRef.current.focus();
    }
  }, [isLocationSelectFocused]);

  useEffect(() => {
    if (isStartTimeDropdownOpen && startTimeSearchInputRef.current) {
      startTimeSearchInputRef.current.focus(); // Auto-focus start time search input when dropdown opens
    }
  }, [isStartTimeDropdownOpen]);

  useEffect(() => {
    if (isEndTimeDropdownOpen && endTimeSearchInputRef.current) {
      endTimeSearchInputRef.current.focus(); // Auto-focus end time search input when dropdown opens
    }
  }, [isEndTimeDropdownOpen]);

  const locationsWithWorkType = useMemo(() => {
    return (availableLocations || []).map(loc => ({
      ...loc,
      workType: loc.workType || ''
    }));
  }, [availableLocations]);

  const scheduledCareWorkerIds = useMemo(() => {
    const scheduledIds = new Set();
    const daySchedules = [...schedules, ...pendingSchedules].filter(s => s.date === dayDate && s.id !== formData.id);
    daySchedules.forEach(schedule => {
      (schedule.careWorkers || []).forEach(id => scheduledIds.add(id));
    });
    return scheduledIds;
  }, [dayDate, schedules, pendingSchedules, formData.id]);

 const filteredStartTimes = useMemo(() => {
  return timeOptions.filter(time =>
    time.replace(/[^0-9]/g, '').includes(startTimeSearch.replace(/[^0-9]/g, ''))
  );
}, [startTimeSearch, timeOptions]);

const filteredEndTimes = useMemo(() => {
  return timeOptions.filter(time =>
    time.replace(/[^0-9]/g, '').includes(endTimeSearch.replace(/[^0-9]/g, ''))
  );
}, [endTimeSearch, timeOptions]);


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox' && name === 'careWorker') {
      setFormData(prev => ({
        ...prev,
        careWorkers: checked
          ? [...prev.careWorkers, value]
          : prev.careWorkers.filter(id => id !== value),
      }));
      const wasSearching = careWorkerSearch.trim() !== '';
      setCareWorkerSearch('');
      if (wasSearching) {
    setIsCareWorkerDropdownOpen(false); // ✅ Close dropdown only after using search
  } else {
    setTimeout(() => {
      careWorkerSearchInputRef.current?.focus(); // Keep focus if not closing
    }, 0);
  }
    } else if (name === 'breakDuration') {
      setFormData(prev => ({
        ...prev,
        breakDuration: Number(value),
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
    setError('');
  };

  const handleTimeSelect = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value })); // Update formData with selected time
    if (name === 'start') {
      setIsStartTimeDropdownOpen(false); // Close start time dropdown
      setStartTimeSearch(''); // Clear start time search input
    } else {
      setIsEndTimeDropdownOpen(false); // Close end time dropdown
      setEndTimeSearch(''); // Clear end time search input
    }
  };

  const handleSubmit = (e) => {
  e.preventDefault();
 
 
  // --- Validations (your existing code) ---
  if (!formData.start || !formData.end) {
    setError('Start and end times are required');
    return;
  }
  // if (formData.start >= formData.end) {
  //   setError('End time must be after start time');
  //   return;
  // }
  if (!formData.careWorkers.length) {
    setError('Please select at least one care worker');
    return;
  }
  // if (!formData.location) {
  //   setError('Location is required');
  //   return;
  // }
 
  // --- Simplified break handling ---
  // formData.breakDuration should already be a number (0, 20, 30, 40)
  // due to the changes in useState initialization and handleChange.
  const numericBreak = formData.breakDuration;
 
  // Ensure it's a valid number just in case (defensive programming)
  // if (typeof numericBreak !== 'number' || isNaN(numericBreak) || numericBreak < 0) {
  //     console.error("breakDuration is not a valid number:", numericBreak);
  //     setError('Invalid break duration. Please select a valid option.');
  //     return;
  // }
 

 
  // --- Your newSchedules construction using numericBreak ---
 const newSchedules = formData.careWorkers.map((id) => {
  const careWorkerId = Array.isArray(id) ? id[0] : id;

  return {
    id: assignment && formData.careWorkers.length === 1 ? formData.id : uuidv4(),
    start: formData.start,
    end: formData.end,
    description: formData.description,
    careWorkers: [careWorkerId],
    careWorker: availableCareWorkers.find((w) => w._id === careWorkerId)?.name || 'Unknown',
    location:
  availableLocations.find((loc) => loc._id === formData.location)?.name ||
  formData.location,
    date: formData.date,
    break: numericBreak,
    isPublished: assignment?.isPublished || false,
    originalScheduleId: assignment?.originalScheduleId,
  };
});

 
  // --- Crucial Debugging Log ---
 
  onSave(newSchedules);
  onClose();
};

  const handleDeleteClick = () => {
    onDelete(assignment);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsCareWorkerDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const today = new Date().toISOString().split('T')[0];
  if (!isOpen) return null;

  const filteredLocations = locationsWithWorkType.filter(loc =>
  `${loc.workType ? `${loc.workType} - ` : ''}${loc.name}`
    .toLowerCase()
    .includes(locationSearch.toLowerCase())
);


const HOURLY_RATE = 12.21;

const calculateTotal = (start, end, breakDuration = 0) => {
  if (!start || !end) return { hours: 0, pay: 0 };

  const [startH, startM] = start.split(":").map(Number);
  const [endH, endM] = end.split(":").map(Number);

  let hours = (endH + endM / 60) - (startH + startM / 60);

  if (hours < 0) hours += 24;


  hours -= breakDuration / 60;

  if (hours < 0) hours = 0; 

  const pay = hours * HOURLY_RATE;
  return {
    hours: Math.round(hours * 100) / 100, // round to 2 decimals
    pay: Math.round(pay * 100) / 100
  };
};

const { hours, pay } = calculateTotal(formData.start, formData.end, formData.breakDuration);


  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex justify-center items-center z-50 p-2 sm:p-4" onClick={onClose}>
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.2 }}
        className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl p-5 sm:p-7 w-full max-w-full sm:max-w-4xl max-h-[95vh] overflow-y-auto border border-gray-200" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-5 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-2.5 rounded-xl shadow-lg">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                {assignment ? (isAdmin ? 'Edit Schedule' : 'View Schedule') : 'Add New Schedule'}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">Fill in the details below</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-lg transition-all duration-200"
            aria-label="Close modal"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
<div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-4">
  <div className="flex items-center gap-2 mb-3">
    <div className="bg-blue-100 p-1.5 rounded-lg">
      <CalendarIcon className="w-4 h-4 text-blue-600" />
    </div>
    <h4 className="font-semibold text-gray-700 text-sm">Date & Workers</h4>
  </div>
  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <div className="w-full sm:w-1/2 flex items-center gap-2 bg-gray-50 rounded-lg p-2">
            <CalendarIcon className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <input
              type="date" 
              id="date"
              name="date" 
              value={formData.date} 
              onChange={handleChange} 
              className="flex-1 w-full px-3 py-2 border-0 bg-transparent rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm text-gray-700"
              required 
              disabled={!isAdmin} 
            />
          </div>

<div className="w-full sm:w-1/2 flex items-center gap-2 bg-gray-50 rounded-lg p-2" ref={dropdownRef}>
  <img
    src="/workers_icons.svg"
    alt="Care Workers"
    className="w-5 h-5 flex-shrink-0 text-blue-600"
  />

  <div className="flex-1 relative">
    <div
      className="w-full px-2 py-1.5 border-0 bg-transparent rounded-md cursor-pointer flex justify-between items-center text-sm"
      tabIndex={0}
      role="button"
      aria-haspopup="listbox"
      aria-expanded={isCareWorkerDropdownOpen}
      onClick={() => isAdmin && setIsCareWorkerDropdownOpen(!isCareWorkerDropdownOpen)}
      onKeyDown={(e) => {
        if (!isAdmin) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setIsCareWorkerDropdownOpen(prev => !prev);
        } else if (e.key === 'Escape' || e.key === 'Tab') {
          setIsCareWorkerDropdownOpen(false);
        }
      }}
    >
      <div className="flex-1 min-w-0">
        {isCareWorkerDropdownOpen ? (
          <input
            ref={careWorkerSearchInputRef}
            type="text"
            value={careWorkerSearch}
            onChange={(e) => setCareWorkerSearch(e.target.value)}
            placeholder="Search care workers..."
            onClick={(e) => e.stopPropagation()}
            className="w-full px-2 py-1 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        ) : (
          <span className="truncate block text-gray-700 text-sm">
            {formData.careWorkers.length > 0
              ? formData.careWorkers
                  .map(id => {
                    const worker = availableCareWorkers.find(w => w._id === id);
                    return worker && worker.role?.toLowerCase() === 'employee' ? worker.name : null;
                  })
                  .filter(Boolean)
                  .join(', ')
              : 'Select Care Workers'}
          </span>
        )}
      </div>

      {isAdmin && (
        <svg
          className={`w-4 h-4 text-gray-500 transform flex-shrink-0 ${isCareWorkerDropdownOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      )}
    </div>

    {/* Dropdown stays inside same relative container */}
    {isCareWorkerDropdownOpen && isAdmin && (
      <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-32 sm:max-h-40 overflow-y-auto z-10">
        {availableCareWorkers
          .filter(worker => worker.role?.toLowerCase() !== 'admin')
          .filter(worker => worker.name.toLowerCase().includes(careWorkerSearch.toLowerCase()))
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((worker) => {
            const isScheduledToday =
              scheduledCareWorkerIds.has(worker._id) && !formData.careWorkers.includes(worker._id);
            const dotColorClass = isScheduledToday ? 'bg-red-500' : 'bg-green-500';
            const statusText = isScheduledToday ? 'Overlapping' : 'Available';
            const textColorClass = isScheduledToday ? 'text-red-700' : 'text-green-700';

            return (
              <label
                key={worker._id}
                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 cursor-pointer transition-colors duration-150"
                title={`${worker.name} is ${statusText}`}
              >
                <input
                  type="checkbox"
                  name="careWorker"
                  value={worker._id}
                  checked={formData.careWorkers.includes(worker._id)}
                  onChange={handleChange}
                  disabled={isScheduledToday}
                  className="form-checkbox h-4 w-4 accent-blue-600 rounded focus:ring-blue-500"
                />
                <span className={`w-2.5 h-2.5 rounded-full ${dotColorClass} ml-2 mr-2 flex-shrink-0`}></span>
                <div className="flex flex-col ml-0 flex-grow">
                  <span className={`truncate capitalize ${isScheduledToday ? 'text-gray-500' : ''}`}>
                    {worker.name}
                  </span>
                  <span className={`text-xs font-semibold ${textColorClass}`}>
                    {statusText}
                  </span>
                </div>
              </label>
            );
          })}
      </div>
    )}
  </div>
</div>

  </div>
</div>

<div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-4">
  <div className="flex items-center gap-2 mb-3">
    <div className="bg-blue-100 p-1.5 rounded-lg">
      <ClockIcon className="w-4 h-4 text-blue-600" />
    </div>
    <h4 className="font-semibold text-gray-700 text-sm">Location & Break</h4>
  </div>
<div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
          
          <div className="w-full sm:w-1/2 flex items-center gap-2" ref={locationDropdownRef}>
  <img
    src="/location.svg"
    alt="Location"
    className="w-5 h-5 sm:w-6 sm:h-5"
  />

  <div className="flex-1 relative">
    {/* Dropdown list */}
    {isLocationSelectFocused && isAdmin && (
      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-32 sm:max-h-40 overflow-y-auto">
        <div className="p-1">
          <input
            ref={locationSearchInputRef}
            type="text"
            value={locationSearch}
            onChange={(e) => setLocationSearch(e.target.value)}
            placeholder="Search locations..."
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div
          onClick={() => {
            handleChange({ target: { name: 'location', value: 'Time Off' } });
            setIsLocationSelectFocused(false);
            setLocationSearch('');
          }}
          className="px-4 py-2 text-sm hover:bg-blue-50 cursor-pointer"
        >
          Time Off
        </div>

        {locationsWithWorkType
          .filter(loc =>
            `${loc.workType ? `${loc.workType} - ` : ''}${loc.name}`
              .toLowerCase()
              .includes(locationSearch.toLowerCase())
          )
          .map((loc) => (
            <div
              key={loc._id}
              onClick={() => {
                handleChange({ target: { name: 'location', value: loc._id } });
                setIsLocationSelectFocused(false);
                setLocationSearch('');
              }}
              className="px-4 py-2 text-sm hover:bg-blue-50 cursor-pointer"
            >
              {loc.workType ? `${loc.workType} - ` : ''}{loc.name}
            </div>
          ))}
      </div>
    )}

    {/* Input display */}
    <div
      className={`w-full p-1 border border-gray-300 rounded-md bg-white text-xs sm:text-sm cursor-pointer ${!isAdmin ? 'opacity-50 pointer-events-none' : ''}`}
      onClick={() => isAdmin && setIsLocationSelectFocused(prev => !prev)}
    >
      {(() => {
        if (!formData.location) return 'Select a Location';
        if (formData.location === 'Time Off') return 'Time Off';

        const selected = locationsWithWorkType.find(loc => loc._id === formData.location);
        return selected ? `${selected.name}` : formData.location;
      })()}
    </div>
  </div>
</div>

          <div className="w-full sm:w-1/2 flex items-center gap-2">
          
            {/* <label htmlFor="breakDuration" className="flex items-center text-gray-700 text-xs sm:text-sm font-medium mb-1 gap-2"> */}
              <PauseCircleIcon className=" fill-blue-700 text-white w-6 h-6 sm:w-6 sm:h-6" />
              {/* Break Duration */}
            {/* </label> */}
            <select
              id="breakDuration"
              name="breakDuration"
              value={formData.breakDuration}
              onChange={handleChange}
              className="flex-1 w-full p-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
              required
              disabled={!isAdmin}
            >
              <option value="">Select Break Duration</option>
  <option value={0}>0 mins</option>
  <option value={30}>30 mins</option>
  <option value={40}>40 mins</option>
  <option value={60}>60 mins</option>
            </select>
          </div>
</div>
</div>

<div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-4">
  <div className="flex items-center gap-2 mb-3">
    <div className="bg-blue-100 p-1.5 rounded-lg">
      <ClockIcon className="w-4 h-4 text-blue-600" />
    </div>
    <h4 className="font-semibold text-gray-700 text-sm">Time & Break</h4>
  </div>
  <div className="flex gap-3 items-center bg-gray-50 rounded-lg p-3">
    <ClockIcon className="w-5 h-5 text-blue-600 flex-shrink-0" />
  <div className="w-full sm:w-[140px] relative" ref={startTimeDropdownRef}> 
    <div
      className={`w-full p-2 border border-gray-300 rounded-md bg-white text-xs sm:text-sm cursor-pointer flex justify-between items-center ${!isAdmin ? 'opacity-50 pointer-events-none' : ''}`}
      tabIndex={isAdmin ? 0 : -1}
      role="button"
      aria-haspopup="listbox"
      aria-expanded={isStartTimeDropdownOpen}
      onClick={() => isAdmin && setIsStartTimeDropdownOpen(prev => !prev)}
      onKeyDown={(e) => {
  if (!isAdmin) return;
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    setIsStartTimeDropdownOpen(prev => !prev);
  } else if (e.key === 'Escape' || e.key === 'Tab') {
    setIsStartTimeDropdownOpen(false);
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    setIsStartTimeDropdownOpen(true);
    setHighlightedIndex(prev => (prev + 1) % filteredStartTimes.length);
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    setIsStartTimeDropdownOpen(true);
    setHighlightedIndex(prev => (prev - 1 + filteredStartTimes.length) % filteredStartTimes.length);
  }
}}

    >
      <span>{formData.start || 'Select Start Time'}</span> {/* Display selected start time or placeholder */}
      {isAdmin && (
        <svg
          className={`w-3 sm:w-4 h-3 sm:h-4 text-gray-500 transform ${isStartTimeDropdownOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
        </svg> 
      )}
    </div>
    {isStartTimeDropdownOpen && isAdmin && (
      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-32 sm:max-h-40 overflow-y-auto"> {/* Dropdown container, scrollable with max height */}
        <div className="p-1">
          <input
            ref={startTimeSearchInputRef} // Ref for auto-focusing the search input
            type="text"
            value={startTimeSearch}
            onChange={(e) => setStartTimeSearch(e.target.value)} // Update search input state for filtering times
            placeholder="Search start time..."
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" // Styled search input
            onKeyDown={(e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev + 1) % filteredStartTimes.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev - 1 + filteredStartTimes.length) % filteredStartTimes.length);
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      handleTimeSelect('start', filteredStartTimes[highlightedIndex]);
      setIsStartTimeDropdownOpen(false);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsStartTimeDropdownOpen(false);
    }
  }}
          />
        </div>
        {filteredStartTimes.map((time, index) => (
  <div
    key={time}
    onClick={() => handleTimeSelect('start', time)}
    className={`px-4 py-2 text-sm cursor-pointer hover:bg-blue-50 ${
      index === highlightedIndex ? 'bg-blue-100' : ''
    }`}
    onMouseEnter={() => setHighlightedIndex(index)} // update highlight on mouse hover
  >
    {time}
  </div>
))}
      </div>
    )}
  </div>
  <span className="text-gray-500 text-lg font-bold">-</span>
  <div className="w-full sm:w-[135px] relative" ref={endTimeDropdownRef}> {/* Wrapper for end time dropdown, with ref for click-outside detection */}
    <div
      className={`w-full p-2 border border-gray-300 rounded-md bg-white text-xs sm:text-sm cursor-pointer flex justify-between items-center ${!isAdmin ? 'opacity-50 pointer-events-none' : ''}`}
      tabIndex={isAdmin ? 0 : -1}
      role="button"
      aria-haspopup="listbox"
      aria-expanded={isEndTimeDropdownOpen}
      onClick={() => isAdmin && setIsEndTimeDropdownOpen(prev => !prev)} // Toggle end time dropdown on click, disabled for non-admins
      onKeyDown={(e) => {
    if (!isAdmin) return;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsEndTimeDropdownOpen(prev => !prev);
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      setIsEndTimeDropdownOpen(false);
      setHighlightedEndIndex(-1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsEndTimeDropdownOpen(true);
      setHighlightedEndIndex(prev => (prev + 1) % filteredEndTimes.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIsEndTimeDropdownOpen(true);
      setHighlightedEndIndex(prev => (prev - 1 + filteredEndTimes.length) % filteredEndTimes.length);
    }
  }}
    >
      <span>{formData.end || 'Select End Time'}</span> {/* Display selected end time or placeholder */}
      {isAdmin && (
        <svg
          className={`w-3 sm:w-4 h-3 sm:h-4 text-gray-500 transform ${isEndTimeDropdownOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
        </svg> 
      )}
    </div>
    {isEndTimeDropdownOpen && isAdmin && (
      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-32 sm:max-h-40 overflow-y-auto"> {/* Dropdown container, scrollable with max height */}
        <div className="p-1">
          <input
            ref={endTimeSearchInputRef} // Ref for auto-focusing the search input
            type="text"
            value={endTimeSearch}
            onChange={(e) => setEndTimeSearch(e.target.value)} // Update search input state for filtering times
            placeholder="Search end time..."
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" // Styled search input
            onKeyDown={(e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedEndIndex(prev => (prev + 1) % filteredEndTimes.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedEndIndex(prev => (prev - 1 + filteredEndTimes.length) % filteredEndTimes.length);
    } else if (e.key === 'Enter' && highlightedEndIndex >= 0) {
      e.preventDefault();
      handleTimeSelect('end', filteredEndTimes[highlightedEndIndex]);
      setIsEndTimeDropdownOpen(false);
      setHighlightedEndIndex(-1);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsEndTimeDropdownOpen(false);
      setHighlightedEndIndex(-1);
    }
  }}
          />
        </div>
        {filteredEndTimes.map((time, index) => (
  <div
    key={time}
    onClick={() => {
      handleTimeSelect('end', time);
      setIsEndTimeDropdownOpen(false);
      setHighlightedEndIndex(-1);
    }}
    className={`px-4 py-2 text-sm cursor-pointer hover:bg-blue-50 ${
      index === highlightedEndIndex ? 'bg-blue-100' : ''
    }`}
    onMouseEnter={() => setHighlightedEndIndex(index)}
  >
    {time}
  </div>
))}

      </div>
    )}
  </div>
</div>
          {error && (
            <div className="mb-1 sm:mb-1 text-red-500 text-xs sm:text-sm">{error}</div>
          )}
          <div className="mb-1 sm:mb-1 mt-2 flex items-center gap-2">
  <BookOpenIcon className="w-6 h-6 fill-blue-700 text-white" />
  
  <textarea
    id="description"
    name="description"
    value={formData.description}
    onChange={handleChange}
    rows="1"
    className="flex-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
    placeholder="Add shift note"
    disabled={!isAdmin}
  ></textarea>
  
</div>
<div className="mt-5 text-sm text-gray-600">
  Total <span className="font-medium text-black">{hours}h </span>
  <span className="font-medium text-black">£{pay.toFixed(2)}</span>
</div>
</div>

          <div className="flex flex-col sm:flex-row justify-between items-center mt-2 sm:mt-3 gap-2">
            {assignment && isAdmin && (
              <button
                type="button"
                onClick={handleDeleteClick}
                className="bg-red-500 hover:bg-red-600 text-white font-medium py-1.5 sm:py-2 px-3 sm:px-4 rounded-md transition-colors duration-200 text-xs sm:text-sm w-full sm:w-auto"
                disabled={!isAdmin}
              >
                Delete
              </button>
            )}
            <div className="w-full flex justify-end mt-4">
  <div className="flex gap-2 w-full sm:w-auto">
    <button
      type="button"
      onClick={onClose}
      className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-1.5 sm:py-2 px-3 sm:px-4 rounded-md transition-colors duration-200 text-xs sm:text-sm flex-1 sm:flex-none"
    >
      Cancel
    </button>
    {isAdmin && (
      <button
        type="submit"
        className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-1.5 sm:py-2 px-3 sm:px-4 rounded-md transition-colors duration-200 text-xs sm:text-sm flex-1 sm:flex-none"
        disabled={!isAdmin}
      >
        Save
      </button>
    )}
  </div>
</div>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

const NewAreaModal = ({ isOpen, onClose, availableCareWorkers, setLocations }) => {
  const [formData, setFormData] = useState({
    name: '',
    workType: '',
    preferredTeamMember: '',
  });
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.workType) {
      setError('Name and Work Type are required');
      return;
    }
    try {
      const newLocation = {
        name: formData.name,
        workType: formData.workType,
        preferredTeamMember: formData.preferredTeamMember || null,
        address: { street: '', city: '' },
      };
      await addLocation(newLocation);
      const locationsResponse = await fetchLocations();
      setLocations(locationsResponse.data.data || []);
      onClose();
    } catch (err) {
      console.error('Error saving location:', err);
      setError(err.response?.data?.msg || 'Failed to save location');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 w-full max-w-full sm:max-w-md h-full sm:h-auto overflow-y-auto" onClick={(e) => e.stopPropagation()} >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Add New Location/Area</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl sm:text-2xl leading-none"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="mb-3 sm:mb-4">
            <label htmlFor="name" className="block text-gray-700 text-xs sm:text-sm font-medium mb-1">
              Location/Area Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
              required
            />
          </div>
          <div className="mb-3 sm:mb-4">
            <label htmlFor="workType" className="block text-gray-700 text-xs sm:text-sm font-medium mb-1">
              Work Type
            </label>
            <select
              id="workType"
              name="workType"
              value={formData.workType}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
              required
            >
              <option value="">Select a Work Type</option>
              <option value="Support Worker">Support Worker</option>
              <option value="Nurse">Nurse</option>
              <option value="Non-Working">Non-Working</option>
            </select>
          </div>
          <div className="mb-3 sm:mb-4">
            <label htmlFor="preferredTeamMember" className="block text-gray-700 text-xs sm:text-sm font-medium mb-1">
              Preferred Team Member
            </label>
            <select
              id="preferredTeamMember"
              name="preferredTeamMember"
              value={formData.preferredTeamMember}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
            >
              <option value="">Select a Team Member (Optional)</option>
              {availableCareWorkers.map((worker) => (
                <option key={worker._id} value={worker._id}>
                  {worker.name}
                </option>
              ))}
            </select>
          </div>
          {error && (
            <div className="mb-3 sm:mb-4 text-red-500 text-xs sm:text-sm">{error}</div>
          )}
          <div className="flex flex-col sm:flex-row justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-1.5 sm:py-2 px-3 sm:px-4 rounded-md text-xs sm:text-sm w-full sm:w-auto"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-1.5 sm:py-2 px-3 sm:px-4 rounded-md text-xs sm:text-sm w-full sm:w-auto"
            >
              Save Location/Area
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ConfirmDeleteModal = ({ isOpen, onClose, onConfirm }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-[500] p-2 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 w-full max-w-full sm:max-w-sm h-full sm:h-auto overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Confirm Deletion</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl sm:text-2xl leading-none"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>
        <p className="text-gray-700 text-xs sm:text-sm mb-4 sm:mb-6">
          Are you sure you want to delete this schedule? This action cannot be undone.
        </p>
        <div className="flex flex-col sm:flex-row justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-1.5 sm:py-2 px-3 sm:px-4 rounded-md text-xs sm:text-sm w-full sm:w-auto"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="bg-red-600 hover:bg-red-700 text-white font-medium py-1.5 sm:py-2 px-3 sm:px-4 rounded-md text-xs sm:text-sm w-full sm:w-auto"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const ConfirmPublishModal = ({ isOpen, onClose, onConfirm }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-[500] p-2 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 w-full max-w-full sm:max-w-sm h-full sm:h-auto overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Confirm Publish</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl sm:text-2xl leading-none"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>
        <p className="text-gray-700 text-xs sm:text-sm mb-4 sm:mb-6">
          Are you sure you want to publish these schedules? This action will make them visible to all relevant team members.
        </p>
        <div className="flex flex-col sm:flex-row justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-1.5 sm:py-2 px-3 sm:px-4 rounded-md text-xs sm:text-sm w-full sm:w-auto"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="bg-green-600 hover:bg-green-700 text-white font-medium py-1.5 sm:py-2 px-3 sm:px-4 rounded-md text-xs sm:text-sm w-full sm:w-auto"
          >
            Publish
          </button>
        </div>
      </div>
    </div>
  );
};

const SchedulePage = () => {
  const [schedules, setSchedules] = useState([]);
  const [pendingSchedules, setPendingSchedules] = useState(() => {
  const saved = localStorage.getItem('pendingSchedules');
  return saved ? JSON.parse(saved) : [];
});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNewAreaModalOpen, setIsNewAreaModalOpen] = useState(false);
  const [isConfirmPublishModalOpen, setIsConfirmPublishModalOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [modalDayDate, setModalDayDate] = useState(null);
  const [modalLocation, setModalLocation] = useState(null);
  const [weekStartDate, setWeekStartDate] = useState(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + daysToMonday);
    return monday;
  });
  const [isHeaderLocationDropdownOpen, setIsHeaderLocationDropdownOpen] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [openLocationDropdowns, setOpenLocationDropdowns] = useState(new Set());
  const [error, setError] = useState(null);
  const [careWorkers, setCareWorkers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dataFetched, setDataFetched] = useState(false);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);
  const [scheduleFilters, setScheduleFilters] = useState({});
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [hasLoadedStaticData, setHasLoadedStaticData] = useState(false);
const [hasInitializedSelectedLocations, setHasInitializedSelectedLocations] = useState(false);
const [previousSelectedLocations, setPreviousSelectedLocations] = useState([]);
const [copiedSchedule, setCopiedSchedule] = useState(null);
const [hoveredWorker, setHoveredWorker] = useState(null);
const [locationSearch, setLocationSearch] = useState('');
const [searchQuery, setSearchQuery] = useState("");
const [remindMessage, setRemindMessage] = useState('');
  const [isRemindLoading, setIsRemindLoading] = useState(false);
const [dropErrorModal, setDropErrorModal] = useState({
  open: false,
  message: '',
});

const [showSearch, setShowSearch] = useState(false);
  const [inputValue, setInputValue] = useState("");

const [isPublishConsentOpen, setIsPublishConsentOpen] = useState(false);
const [notificationPreferences, setNotificationPreferences] = useState({
  email: false,
  mobileNotification: false,
  sms: false,
  whatsapp: false,
});
const [showDatePicker, setShowDatePicker] = useState(false);
const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);

  // Auth context
  const { user, token, isLoading: isAuthLoading } = useAuth();

  // Refs
  const initialFetchDone = useRef(false);
  const locationDropdownRef = useRef(null);
  const headerLocationSearchInputRef = useRef(null);

  const fetchInitialData = useCallback(async () => {
    if (!user || !user.id) {
      setError('Please log in to view schedules');
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      // Fetch care workers and locations only if not already fetched
      if (!initialFetchDone.current) {
        const usersResponse = await fetchAllUsers();
        const fetchedCareWorkers = usersResponse.data.data.map(user => ({
          _id: user._id,
          name: user.name || `${user.firstName} ${user.lastName}`,
          role:user.access,
          avatarStyle: user.avatarStyle || null,
        }));
        setCareWorkers(fetchedCareWorkers);

        const locationsResponse = await fetchLocations();
        setLocations(locationsResponse.data.data || []);
      }

      // Fetch schedules for the current week
      const start = new Date(weekStartDate);
      const end = new Date(weekStartDate);
      end.setDate(weekStartDate.getDate() + 6);
      const schedulesResponse = await fetchSchedulesInRange(start, end);

      const transformedSchedules = [];
      schedulesResponse.data.data.forEach(schedule => {
        const scheduleLocation = schedule.location?.name || (typeof schedule.location === 'string' ? schedule.location : 'Time Off');
        if (!schedule.careWorker) {
          if (user.role === 'admin') {
            transformedSchedules.push({
              id: schedule._id,
              start: schedule.start || '',
              end: schedule.end || '',
              description: schedule.description || '',
              careWorkers: [],
              careWorker: 'None',
              location: scheduleLocation,
              date: schedule.date || '',
              breakDuration: schedule.break ? `${schedule.break} mins` : '',
              isPublished: schedule.isPublished || false,
              originalScheduleId: schedule._id,
            });
          }
          return;
        }

        const careWorkerIds = Array.isArray(schedule.careWorker)
          ? schedule.careWorker.map(cw => cw._id || cw).filter(Boolean)
          : [schedule.careWorker?._id || schedule.careWorker].filter(Boolean);

        careWorkerIds.forEach(careWorkerId => {
          if (user.role !== 'admin' && careWorkerId !== user.id) {
            return;
          }
          transformedSchedules.push({
            id: schedule._id + '-' + careWorkerId,
            start: schedule.start || '',
            end: schedule.end || '',
            description: schedule.description || '',
            careWorkers: [careWorkerId],
            careWorker: careWorkers.find(w => w._id === careWorkerId)?.name || 'Unknown',
            location: scheduleLocation,
            date: schedule.date || '',
            breakDuration: schedule.break ? `${schedule.break} mins` : '',
            isPublished: schedule.isPublished || false,
            originalScheduleId: schedule._id,
          });
        });
      });

      setSchedules(transformedSchedules);
      setDataFetched(true);
      initialFetchDone.current = true; // Mark initial fetch as done
    } catch (err) {
      console.error('Error fetching initial data:', err);
      const message = err.response?.status === 401
        ? 'Not authorized. Please log in.'
        : err.response?.data?.msg || 'Failed to load data';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [user, weekStartDate, careWorkers]);

  // Fetch schedules for a specific week
  const fetchSchedulesForWeek = useCallback(async (startDate) => {
    try {
      setIsLoading(true);
      const start = new Date(startDate);
      const end = new Date(startDate);
      end.setDate(startDate.getDate() + 6);
      const schedulesResponse = await fetchSchedulesInRange(start, end);

      const transformedSchedules = [];
      schedulesResponse.data.data.forEach(schedule => {
        const scheduleLocation = schedule.location?.name || (typeof schedule.location === 'string' ? schedule.location : 'Time Off');
        if (!schedule.careWorker) {
          if (user.role === 'admin') {
            transformedSchedules.push({
              id: schedule._id,
              start: schedule.start || '',
              end: schedule.end || '',
              description: schedule.description || '',
              careWorkers: [],
              careWorker: 'None',
              location: scheduleLocation,
              date: schedule.date || '',
              breakDuration: schedule.break ? `${schedule.break} mins` : '',
              isPublished: schedule.isPublished || false,
              originalScheduleId: schedule._id,
            });
          }
          return;
        }

        const careWorkerIds = Array.isArray(schedule.careWorker)
          ? schedule.careWorker.map(cw => cw._id || cw).filter(Boolean)
          : [schedule.careWorker?._id || schedule.careWorker].filter(Boolean);

        careWorkerIds.forEach(careWorkerId => {
          if (user.role !== 'admin' && careWorkerId !== user.id) {
            return;
          }
          transformedSchedules.push({
            id: schedule._id + '-' + careWorkerId,
            start: schedule.start || '',
            end: schedule.end || '',
            description: schedule.description || '',
            careWorkers: [careWorkerId],
            careWorker: careWorkers.find(w => w._id === careWorkerId)?.name || 'Unknown',
            location: scheduleLocation,
            date: schedule.date || '',
            breakDuration: schedule.break ? `${schedule.break} mins` : '',
            isPublished: schedule.isPublished || false,
            originalScheduleId: schedule._id,
          });
        });
      });

      setSchedules(transformedSchedules);
      setError(null);
    } catch (err) {
      console.error('Error fetching schedules:', err);
      const message = err.response?.status === 401
        ? 'Not authorized. Please log in.'
        : err.response?.data?.msg || 'Failed to load schedules';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [user, careWorkers]);

  // Initial data fetch
  useEffect(() => {
    if (!initialFetchDone.current) {
      fetchInitialData();
    }
  }, [fetchInitialData]);

  // Update selected locations after initial data fetch
  useEffect(() => {
    if (dataFetched && !isLoading) {
      if (user.role !== 'admin') {
        const userLocations = [...new Set(
          schedules
            .filter(s => s.careWorkers.includes(user.id))
            .map(s => {
              const loc = locations.find(loc => loc.name === s.location);
              if (!loc) {
                return null;
              }
              return loc._id;
            })
            .filter(Boolean)
        )];
        const newSelectedLocations = userLocations.length > 0 ? userLocations : ['time-off'];
        setSelectedLocations(newSelectedLocations);
        setOpenLocationDropdowns(new Set(userLocations || 'time-off'));
      } else {
        const storedAdminLocations = localStorage.getItem('adminSelectedLocations');
        let initialAdminLocations = ['time-off']; // Default if nothing is found or invalid
        

        if (storedAdminLocations) {
          try {
            const parsedLocations = JSON.parse(storedAdminLocations);
            // Validate: ensure it's an array and contains strings
            if (Array.isArray(parsedLocations) && parsedLocations.every(item => typeof item === 'string')) {
              initialAdminLocations = parsedLocations;
            } else {
              console.warn("Stored admin locations corrupted or invalid, defaulting.");
            }
          } catch (error) {
            console.error("Error parsing stored admin locations:", error);
          }
        }
        setSelectedLocations(initialAdminLocations);
         const locationsToOpen = initialAdminLocations.filter(id => id !== 'time-off');
      setOpenLocationDropdowns(new Set(locationsToOpen));

        // Crucial: Initialize previousSelectedLocations for future comparison
        setPreviousSelectedLocations(initialAdminLocations.sort());
      }
      setHasInitializedSelectedLocations(true); // Mark as initialized
    }
    // Dependencies: Ensure this re-runs if necessary data changes BEFORE initialization
  }, [isLoading, hasLoadedStaticData, schedules, locations, user, hasInitializedSelectedLocations]);

  useEffect(() => {
   
    if (user.role === 'admin' && hasInitializedSelectedLocations && !isLoading) {
      const currentLocationsSorted = [...selectedLocations].sort();
      const prevLocationsSorted = [...previousSelectedLocations].sort();

      if (JSON.stringify(currentLocationsSorted) !== JSON.stringify(prevLocationsSorted)) {
        localStorage.setItem('adminSelectedLocations', JSON.stringify(currentLocationsSorted)); 
        setPreviousSelectedLocations(currentLocationsSorted);
      }
    }
  }, [selectedLocations, user.role, isLoading, hasInitializedSelectedLocations, previousSelectedLocations]);
  useEffect(() => {
  const handleClickOutside = (event) => {
    if (
      locationDropdownRef.current &&
      !locationDropdownRef.current.contains(event.target)
    ) {
      setIsHeaderLocationDropdownOpen(false); // Close dropdown
    }
  };

  if (isHeaderLocationDropdownOpen) {
    document.addEventListener('mousedown', handleClickOutside);
  }

  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };
}, [isHeaderLocationDropdownOpen]);
useEffect(() => {
    if (isHeaderLocationDropdownOpen && headerLocationSearchInputRef.current) {
      headerLocationSearchInputRef.current.focus();
    }
  }, [isHeaderLocationDropdownOpen]);
  useEffect(() => {
  localStorage.setItem('pendingSchedules', JSON.stringify(pendingSchedules));
}, [pendingSchedules]);

  const allAvailableAreas = useMemo(() => locations || [], [locations]);

  const currentVisibleLocations = useMemo(() => {
    const filteredLocations = (allAvailableAreas || []).filter((loc) => selectedLocations.includes(loc._id));
    return [
      { _id: 'time-off', name: 'Time Off', workType: '' },
      ...filteredLocations
    ];
  }, [allAvailableAreas, selectedLocations]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const date = new Date(weekStartDate);
    date.setDate(weekStartDate.getDate() + i);
    return {
      dateStr: date.toISOString().split('T')[0],
      display: date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' }),
      shortDay: date.toLocaleDateString('en-US', { weekday: 'short' }),
      dayNum: date.getDate(),
    };
  }), [weekStartDate]);

  const isAdmin = user && user.role === "admin";

  const weekRangeLabel = useCallback(() => {
    const start = new Date(weekStartDate);
    const end = new Date(weekStartDate);
    end.setDate(weekStartDate.getDate() + 6);
    const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startStr} - ${endStr}`;
  }, [weekStartDate]);

  const handlePreviousWeek = useCallback((e) => {
    e.preventDefault();
    const newStartDate = new Date(weekStartDate);
    newStartDate.setDate(weekStartDate.getDate() - 7);
    setWeekStartDate(newStartDate);
    fetchSchedulesForWeek(newStartDate);
  }, [weekStartDate, fetchSchedulesForWeek]);

  const handleNextWeek = useCallback((e) => {
    e.preventDefault();
    const newStartDate = new Date(weekStartDate);
    newStartDate.setDate(weekStartDate.getDate() + 7);
    setWeekStartDate(newStartDate);
    fetchSchedulesForWeek(newStartDate);
  }, [weekStartDate, fetchSchedulesForWeek]);

  const handleDateSelect = useCallback((e) => {
    const selectedDate = new Date(e.target.value);
    const dayOfWeek = selectedDate.getDay();
    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(selectedDate);
    monday.setDate(selectedDate.getDate() + daysToMonday);
    setWeekStartDate(monday);
    fetchSchedulesForWeek(monday);
    setShowDatePicker(false);
  }, [fetchSchedulesForWeek]);

  const handleRefresh = useCallback(() => {
    initialFetchDone.current = false; // Allow full data fetch
    fetchInitialData();
  }, [fetchInitialData]);

    // const formatTime = useCallback((time) => {
    //   if (!time) return '';
    //   const [hours, minutes] = time.split(':').map(Number);
    //   const period = hours >= 12 ? 'PM' : 'AM';
    //   const adjustedHours = hours % 12 || 12;
    //   return `${adjustedHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    // }, []);
  const formatTime = useCallback((time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':').map(Number);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }, []);

  const calculateHoursWorked = useCallback((careWorkerName) => {
    const allSchedules = [...schedules, ...pendingSchedules];
    const workerSchedules = allSchedules.filter((s) => {
      if (!s.careWorkers || !Array.isArray(s.careWorkers)) return false;
      return s.careWorkers.some(id => {
        const worker = careWorkers.find(w => w._id === id);
        return worker && worker.name === careWorkerName;
      });
    });
    let totalHours = 0;
    workerSchedules.forEach((schedule) => {
      if (schedule.start && schedule.end) {
        const start = new Date(`1970-01-01T${schedule.start}:00`);
        const end = new Date(`1970-01-01T${schedule.end}:00`);
        const diffMs = end - start;
        const hours = diffMs / (1000 * 60 * 60);
        totalHours += hours > 0 ? hours : 0;
      }
    });
    return totalHours.toFixed(2);
  }, [schedules, pendingSchedules, careWorkers]);

  const handleExport = useCallback(() => {
    // console.log("user details in handle export ",user?.organization?.name)
  const OrganizationName = (user?.organization?.name || 'Care Team').toUpperCase();
  const allSchedules = [...schedules, ...pendingSchedules];

  // Map selected location IDs to names
  const selectedLocationNames = locations
    .filter(loc => selectedLocations.includes(loc._id))
    .map(loc => loc.name);


  const weekSchedules = allSchedules
    .filter((schedule) => {
      if (!schedule.date) {
        // console.log("Skipping schedule (no date):", schedule);
        return false;
      }

      const isInWeek = days.some((day) => day.dateStr === schedule.date);
      // if (!isInWeek) {
      //   console.log("Skipping schedule (not in week):", schedule.date);
      //   return false;
      // }

      // Compare by name
      const matchesLocation =
        selectedLocationNames.length === 0 || selectedLocationNames.includes(schedule.location);

      // if (!matchesLocation) {
      //   console.log(`Skipping schedule (location filter fail):`, schedule.location);
      // }

      return matchesLocation;
    })
    .sort((a, b) => {
      const dateCompare = a.date.localeCompare(b.date);
      if (dateCompare !== 0) return dateCompare;
      const locationCompare = a.location.localeCompare(b.location);
      if (locationCompare !== 0) return locationCompare;
      return a.start.localeCompare(b.start);
    });

  if (weekSchedules.length === 0) {
    setError("No schedules available to export for this week.");
    return;
  }
// currentVisibleLocations.forEach(area => {
//   console.log("Export area:", area._id, area.name);
// });

  // if (weekSchedules.length === 0) {
  //   setError("No schedules available to export for this week.");
  //   return;
  // }


const groupedByDate = weekSchedules.reduce((acc, schedule) => {
  const dateKey = new Date(schedule.date).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  });
  if (!acc[dateKey]) acc[dateKey] = [];
  acc[dateKey].push(schedule);
  return acc;
}, {});
const groupedByLocation = currentVisibleLocations.filter(area => area._id !== 'time-off').map(area => {
  const daysMap = {};
  days.forEach(day => {
    daysMap[day.dateStr] = weekSchedules.filter(s =>
      s.date === day.dateStr &&
      s.location === area.name
    );
  });
  return { area, daysMap };
});
const htmlContent = `
<html>
<head>
  <title>Weekly Schedule - ${weekRangeLabel()}</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
    h1 { text-align: center; font-size: 16px; margin-bottom: 20px; }
    .days-header { display: grid; grid-template-columns: repeat(7, 1fr); gap: 8px; font-weight: bold; text-align: center; margin-bottom: 8px; }
    .area-block { margin-bottom: 20px; background: #fff; border: 1px solid #ddd; border-radius: 4px; }
    .area-title { padding: 8px; font-weight: bold; border-bottom: 2px solid #ddd; background: #f9f9f9; }
    .schedule-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 8px; padding: 8px; }
    .day-cell { border: 1px solid #ddd; border-radius: 4px; min-height: 100px; padding: 4px; background: #fff; }
    .shift { border: 1px solid #ccc; border-radius: 4px; padding: 4px; margin-bottom: 4px; font-size: 10px; }
    .time { font-weight: bold; }
    .worker { font-size: 10px; }
  </style>
</head>
<body>
  <h1>${OrganizationName}<br> ${weekRangeLabel()}</h1>

  <div class="days-header">
    ${days.map(day => `<div>${day.shortDay} ${day.dayNum}</div>`).join('')}
  </div>

  ${groupedByLocation.map(({ area, daysMap }) => `
    <div class="area-block">
      <div class="area-title">${area.workType ? `${area.workType} - ` : ''}${area.name}</div>
      <div class="schedule-grid">
        ${days.map(day => `
          <div class="day-cell">
            ${(daysMap[day.dateStr] || []).map(s => `
              <div class="shift">
                <div class="time">${formatTime(s.start)} - ${formatTime(s.end)}</div>
                <div class="worker">${s.careWorker || 'None'}</div>
              </div>
            `).join('')}
          </div>
        `).join('')}
      </div>
    </div>
  `).join('')}
</body>
</html>
`;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setError('Failed to open print window. Please allow pop-ups for this site.');
      return;
    }
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  }, [schedules, pendingSchedules, days, weekRangeLabel, formatTime,selectedLocations,locations]);
  
  const handleRemindClick = async () => {
    setRemindMessage('');
    setIsRemindLoading(true);

    try {
      // Calculate dates
      const today = new Date();
      const currentDay = today.getDay(); 
     const startDate = today.toISOString().split('T')[0];
      const lastDayOfWeek = new Date(today);
    const diffToSunday = 7 - currentDay === 7 ? 0 : 7 - currentDay; 
    lastDayOfWeek.setDate(today.getDate() + diffToSunday);
      const endDate = lastDayOfWeek.toISOString().split('T')[0];
      
      const result = await remindAssignedShifts(startDate, endDate);
      
      setRemindMessage(result.data.message);
      
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
      setRemindMessage(`Error: ${errorMessage}`);
      
    } finally {
      setIsRemindLoading(false);
    }
  };

  const handleAddSchedule = useCallback((dayDate, locationName) => {
    if (!isAdmin) return;
    setModalDayDate(dayDate);
    setModalLocation(locationName);
    setSelectedSchedule(null);
    setIsModalOpen(true);
  }, [isAdmin]);

  const handleEditSchedule = useCallback((schedule) => {
    if (!isAdmin && !schedule.isPublished) return;
    setModalDayDate(schedule.date);
    setModalLocation(schedule.location);
    setSelectedSchedule(schedule);
    setIsModalOpen(true);
  }, [isAdmin]);

  const handleSaveSchedule = useCallback(async (newSchedules) => {
    if (!isAdmin) return;
    try {
      if (newSchedules[0].isPublished) {
        for (const newSchedule of newSchedules) {
          const locationObject = locations.find(loc => loc.name === newSchedule.location);

        const locationId = locationObject ? locationObject._id : null;

        if (!locationId) {
            console.error("Error: Location ID not found for name:", newSchedule.location);
            continue;
        }
          const updatedSchedule = {
            start: newSchedule.start,
            end: newSchedule.end,
            description: newSchedule.description,
            careWorker: newSchedule.careWorkers,
            location: locationId,
            date: newSchedule.date,
            break: newSchedule.break,
          };
          const response = await updateSchedule(newSchedule.originalScheduleId || newSchedule.id.split('-')[0], updatedSchedule);

          setSchedules((prev) => {
            const index = prev.findIndex((s) => s.id === newSchedule.id);
            if (index !== -1) {
              const updated = [...prev];
              updated[index] = {
                id: response.data._id + '-' + newSchedule.careWorkers[0],
                start: response.data.start || '',
                end: response.data.end || '',
                description: response.data.description || '',
                careWorkers: [newSchedule.careWorkers[0]],
                careWorker: careWorkers.find(w => w._id === newSchedule.careWorkers[0])?.name || 'Unknown',
                location: response.data.location?.name || newSchedule.location,
                date: response.data.date || '',
                breakDuration: response.data.break ? `${response.data.break} mins` : '',
                isPublished: response.data.isPublished || true,
                originalScheduleId: response.data._id,
              };
              return updated;
            }
            return [...prev, {
              ...newSchedule,
              careWorker: careWorkers.find(w => w._id === newSchedule.careWorkers[0])?.name || 'Unknown',
              originalScheduleId: response.data._id,
            }];
          });
        }
      } else {
        setPendingSchedules((prev) => {
          const newIds = newSchedules.map(s => s.id);
          const filteredPrev = prev.filter(s => !newIds.includes(s.id));
          const updated = [...filteredPrev, ...newSchedules];
          return updated;
        });
        const locationName = newSchedules[0].location;
        const location = locations.find(loc => loc.name === locationName);
        if (location) {
          setOpenLocationDropdowns(prev => new Set([...prev, location._id]));
        }
      }
      setError(null);
    } catch (err) {
      console.error('Error saving schedules:', err);
      const message = err.response?.status === 401
        ? 'Not authorized. Please log in as admin or manager.'
        : err.response?.data?.msg || 'Failed to save schedules';
      setError(message);
    }
  }, [isAdmin, careWorkers, locations]);

  const handleDeleteSchedule = useCallback((scheduleItemToDelete) => {
    if (!isAdmin) return;
    setScheduleToDelete(scheduleItemToDelete);
    setIsConfirmDeleteModalOpen(true);
  }, [isAdmin]);

  const confirmDeleteSchedule = useCallback(async () => {
    if (!scheduleToDelete) return;

    try {
      const idForBackendDeletion = scheduleToDelete.originalScheduleId || scheduleToDelete._id;
      const idForLocalDeletion = scheduleToDelete.id;

      const isPendingLocally = !scheduleToDelete._id && !scheduleToDelete.originalScheduleId &&
                               pendingSchedules.some(s => s.id === idForLocalDeletion);

      if (isPendingLocally) {
        setPendingSchedules(prev => prev.filter(s => s.id !== idForLocalDeletion));
      } else {
        if (!idForBackendDeletion) {
          setError("Error: Cannot delete. Schedule is missing a valid database ID (_id or originalScheduleId).");
          console.error("Persisted schedule is missing _id or originalScheduleId:", scheduleToDelete);
          return;
        }

        await deleteSchedule(idForBackendDeletion);
        setSchedules(prev => prev.filter(s => 
          s.id !== idForLocalDeletion && 
          s.originalScheduleId !== idForBackendDeletion && 
          s._id !== idForBackendDeletion
        ));
      }

      setError(null);
      setIsConfirmDeleteModalOpen(false);
      setScheduleToDelete(null);
      setIsModalOpen(false);
    } catch (err) {
      console.error('Error in confirmDeleteSchedule:', err);
      let message = 'Failed to delete schedule due to an unexpected error.';
      if (err.response) {
        if (err.response.status === 401) {
          message = 'Not authorized. You must be an admin or manager to delete schedules.';
        } else if (err.response.status === 404) {
          message = 'Schedule not found or already deleted on the server.';
        } else if (err.response.status === 400 && err.response.data?.message?.includes('Cast to ObjectId failed')) {
          message = 'Internal error: Invalid ID format sent to server.';
        } else {
          message = err.response.data?.msg || err.response.data?.message || message;
        }
      } else if (err.request) {
        message = 'Network error: Could not reach the server. Please check your internet connection.';
      } else {
        message = err.message || message;
      }
      setError(message);
      setIsConfirmDeleteModalOpen(false);
      setScheduleToDelete(null);
    }
  }, [scheduleToDelete, pendingSchedules, isAdmin]);

  const handlePublishClick = useCallback(() => {
    if (!isAdmin) return;
    if (pendingSchedules.length === 0) {
      setError('No pending schedules to publish.');
      return;
    }
    setIsPublishConsentOpen(true);
  }, [isAdmin, pendingSchedules]);

  const triggerNotificationRefresh = useCallback(() => {
    const event = new CustomEvent(NOTIFICATION_EVENT);
    window.dispatchEvent(event);
  }, []);
  
  const handlePublish = useCallback(async () => {
    setIsConfirmPublishModalOpen(false);
    try {
      const schedulesData = pendingSchedules.map(schedule => {
        if (!schedule.careWorkers?.length) {
          throw new Error('At least one care worker is required.');
        }
        if (!schedule.location) {
          throw new Error('Location is required.');
        }
        if (!schedule.date || !/^\d{4}-\d{2}-\d{2}$/.test(schedule.date)) {
          throw new Error('Invalid date format. Expected YYYY-MM-DD.');
        }
        if (!schedule.start || !schedule.end || !/^([01]\d|2[0-3]):[0-5]\d$/.test(schedule.start) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(schedule.end)) {
          throw new Error('Invalid time format. Expected HH:MM.');
        }
        const breakDuration = schedule.break ? `${schedule.break} mins` : '0 mins';
        return {
          start: schedule.start,
          end: schedule.end,
          description: schedule.description || '',
          careWorkers: schedule.careWorkers,
          location: schedule.location,
          date: schedule.date,
          break: breakDuration,
          id: schedule.id.split('-')[0],
        };
      });
      const createResponse = await createSchedulesBatch(schedulesData);
      const scheduleIds = createResponse.data.data.map(schedule => schedule._id);
      const publishResponse = await publishSchedules(scheduleIds);
      const newSchedules = [];
      publishResponse.data.data.forEach(schedule => {
        const careWorkerIds = Array.isArray(schedule.careWorker)
          ? schedule.careWorker.map(cw => cw._id || cw).filter(Boolean)
          : [schedule.careWorker?._id || schedule.careWorker].filter(Boolean);
        careWorkerIds.forEach(careWorkerId => {
          const careWorkerName = careWorkers.find(w => w._id === careWorkerId)?.name || 'Unknown';
          newSchedules.push({
            id: schedule._id + '-' + careWorkerId,
            start: schedule.start || '',
            end: schedule.end || '',
            description: schedule.description || '',
            careWorkers: [careWorkerId],
            careWorker: careWorkerName,
            location: schedule.location?.name || schedule.location || 'Time Off',
            date: schedule.date || '',
            breakDuration: schedule.break ? `${schedule.break} mins` : '',
            isPublished: true,
            originalScheduleId: schedule._id,
          });
        });
      });
      setSchedules(prev => [...prev, ...newSchedules]);
      setPendingSchedules([]);
      setError(null);

      // Trigger notification refresh
      triggerNotificationRefresh();
    } catch (err) {
      console.error('Publish error:', err);
      const message = err.response?.status === 400
        ? err.response?.data?.msg || 'Invalid schedule data'
        : err.response?.status === 401
          ? 'Not authorized. Please log in as admin or manager.'
          : err.message || 'Failed to publish schedules';
      setError(message);
    }
  }, [pendingSchedules, careWorkers, triggerNotificationRefresh]);

  const handleLocationCheckboxChange = useCallback((locationId) => {
    if (!isAdmin) return;
    setSelectedLocations((prevSelected) => {
      const isSelected = prevSelected.includes(locationId);
      let newSelected;
      if (isSelected) {
        newSelected = prevSelected.filter((id) => id !== locationId);
      } else {
        newSelected = [...prevSelected, locationId];
        setLocationSearch('');
      }
      const finalSelected = newSelected.length > 0 ? newSelected : ['time-off'];
      return finalSelected;
    });
  }, [isAdmin]);

  const handleSelectAllLocations = useCallback(() => {
    if (!isAdmin) return;
    const allLocationIds = allAvailableAreas.map((loc) => loc._id);
    setSelectedLocations((prevSelected) => {
      const newSelected = prevSelected.length === allLocationIds.length ? ['time-off'] : allLocationIds;
      return newSelected;
    });
  }, [isAdmin, allAvailableAreas]);

  const getSortedDaySchedules = useMemo(() => (dateStr, locationName, locationId) => {
    const allSchedules = [...schedules, ...pendingSchedules];
    const filter = scheduleFilters[locationId] || { status: 'all', careWorker: 'all' };
    
    const filteredSchedules = allSchedules
      .filter((s) => {
         const isSameLocation =
  s.location === locationId ||
  s.location === locationName ||
  (typeof s.location === 'object' && (s.location._id === locationId || s.location.name === locationName));

if (s.date !== dateStr || !isSameLocation) return false;


        if (filter.status === 'published' && !s.isPublished) return false;
        if (filter.status === 'unpublished' && s.isPublished) return false;
        if (filter.careWorker !== 'all' && !s.careWorkers.includes(filter.careWorker)) return false;
        return true;
      })
.sort((a, b) => {

  const hourA = new Date(`${a.date}T${a.start}:00`).getHours();
  const hourB = new Date(`${b.date}T${b.start}:00`).getHours();

  if (hourA !== hourB) {
    return hourA - hourB; 
  }

  const nameA = a.careWorker?.toLowerCase() || '';
  const nameB = b.careWorker?.toLowerCase() || '';
  
  // Compare the names for alphabetical sorting (A-Z)
  return nameA.localeCompare(nameB);
});
    
    return filteredSchedules;
    
  }, [schedules, pendingSchedules, scheduleFilters]);

  const toggleLocationDropdown = useCallback((locationId) => {
    if (!isAdmin && !selectedLocations.includes(locationId)) return;
    setOpenLocationDropdowns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(locationId)) {
        newSet.delete(locationId);
        setScheduleFilters(prevFilters => {
          const newFilters = { ...prevFilters };
          delete newFilters[locationId];
          return newFilters;
        });
      } else {
        newSet.add(locationId);
        setScheduleFilters(prevFilters => ({
          ...prevFilters,
          [locationId]: { status: 'all', careWorker: 'all' }
        }));
      }
      return newSet;
    });
  }, [isAdmin, selectedLocations]);

  const dynamicCareWorkers = useMemo(() => {
    const uniqueWorkers = new Set();
    careWorkers.forEach((worker) => uniqueWorkers.add(worker.name));
    [...schedules, ...pendingSchedules].forEach((s) => {
      if (s.careWorker && s.careWorker.trim()) {
        uniqueWorkers.add(s.careWorker);
      }
    });
    const workerList = Array.from(uniqueWorkers).sort();
    return workerList;
  }, [schedules, pendingSchedules, careWorkers]);

  const headerLocationButtonText = useMemo(() => {
    if (!allAvailableAreas || allAvailableAreas.length === 0) {
      return 'Multiple Locations';
    }
    if (selectedLocations.length === 0) {
      return 'Multiple Locations';
    }
    if (selectedLocations.length === 1) {
      const selectedLoc = allAvailableAreas.find((loc) => loc._id === selectedLocations[0]);
      if (!selectedLoc) {
        return 'Multiple Locations';
      }
      return selectedLoc.name;
    }
    if (selectedLocations.length === allAvailableAreas.length) {
      return 'Multiple Locations';
    }
    return `Multiple Locations`;
  }, [selectedLocations, allAvailableAreas]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if user is typing in an input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
        return;
      }

      // Ctrl/Cmd + N: New Schedule
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        setIsModalOpen(true);
        setModalDayDate(days[0]?.dateStr);
        setModalLocation(null);
        setSelectedSchedule(null);
      }

      // Ctrl/Cmd + S: Save/Publish
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (pendingSchedules.length > 0 && isAdmin) {
          setIsPublishConsentOpen(true);
        }
      }

      // Ctrl/Cmd + F: Search
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        setShowSearch(prev => !prev);
      }

      // ESC: Close modals
      if (e.key === 'Escape') {
        setIsModalOpen(false);
        setIsNewAreaModalOpen(false);
        setIsConfirmPublishModalOpen(false);
        setIsConfirmDeleteModalOpen(false);
        setIsPublishConsentOpen(false);
        setShowSearch(false);
        setShowKeyboardHelp(false);
      }

      // ?: Show keyboard shortcuts
      if (e.key === '?' && !e.shiftKey) {
        e.preventDefault();
        setShowKeyboardHelp(true);
      }

      // Arrow Left: Previous week
      if (e.key === 'ArrowLeft' && !isModalOpen) {
        handlePreviousWeek(e);
      }

      // Arrow Right: Next week
      if (e.key === 'ArrowRight' && !isModalOpen) {
        handleNextWeek(e);
      }

      // Ctrl/Cmd + P: Print/Export
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        handleExport();
      }

      // R: Refresh
      if (e.key === 'r' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        fetchSchedulesForWeek(weekStartDate);
      }

      // T: Go to today
      if (e.key === 't' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        const today = new Date();
        const dayOfWeek = today.getDay();
        const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        const monday = new Date(today);
        monday.setDate(today.getDate() + daysToMonday);
        setWeekStartDate(monday);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, pendingSchedules, isAdmin, days, weekStartDate, handlePreviousWeek, handleNextWeek, handleExport, fetchSchedulesForWeek]);


  const handleCopy = (schedule) => {
  setCopiedSchedule(schedule);
};

const handlePaste = (date, locationName, locationId) => {
  if (!copiedSchedule) return;

  const employeeId = copiedSchedule.employeeId || copiedSchedule.careWorkers?.[0];
  const workerName = careWorkers.find(w => w._id === employeeId)?.name || 'Unknown';

  const newSchedule = {
    ...copiedSchedule,
    id: `${copiedSchedule.id.split("-")[0]}-${Date.now()}`,
    originalScheduleId: undefined,
    date,
    location: locationName,
    areaId: locationId,
    isPublished: false,
    careWorkers: [employeeId],
    careWorker: workerName, // ✅ readable name
    start: copiedSchedule.start || "12:00",
    end: copiedSchedule.end || "20:00",
    break: typeof copiedSchedule.breakDuration === "string"
      ? parseInt(copiedSchedule.breakDuration)
      : copiedSchedule.breakDuration || 0,
  };

  // Conflict check
  const isConflict = schedules.some(
    (s) =>
      (s.employeeId || s.careWorkers?.[0]) === employeeId &&
      s.date === date
  );

  if (isConflict) {
    setDropErrorModal({
      open: true,
      message: "This careworker is already scheduled for this day in another location.",
    });
    return;
  }

  handleSaveSchedule([newSchedule]);
  setCopiedSchedule(null);
};




const handleDragEnd = (result) => {

  const { destination, source, draggableId } = result;

  if (!destination) {
    // console.log("❌ No destination — dropped outside any droppable");
    return;
  }

  if (
    destination.droppableId === source.droppableId &&
    destination.index === source.index
  ) {
    // console.log("Dropped in the same location — no changes");
    return;
  }

  const [areaId, date] = destination.droppableId.split("__");

  const draggedIndex = schedules.findIndex(s => s.id === draggableId);
  if (draggedIndex === -1) {
    // console.log("Schedule not found for draggableId:", draggableId);
    return;
  }

  const draggedSchedule = { ...schedules[draggedIndex] };
  // console.log("Dragged Schedule:", draggedSchedule);

  const employeeId = draggedSchedule.employeeId || draggedSchedule.careWorkers?.[0];

  const isConflict = schedules.some(
    s =>
      (s.employeeId || s.careWorkers?.[0]) === employeeId &&
      s.date === date &&
      s.id !== draggedSchedule.id
  );

  if (isConflict) {
    // console.log("Conflict detected: employee already scheduled on this day");
    setDropErrorModal({
      open: true,
      message: "This careworker is already scheduled for this day in another location.",
    });
    return;
  }

  const locationObj = locations.find(loc => loc._id === areaId);
  const locationName = locationObj?.name || draggedSchedule.location || "Unknown";

  // ✅ Here is the crucial part that ensures the location is the areaId
  const scheduleToSave = {
    id: draggedSchedule.originalScheduleId || draggedSchedule.id.split("-")[0],
    start: draggedSchedule.start,
    end: draggedSchedule.end,
    description: draggedSchedule.description || "",
    careWorkers: [employeeId],
    // 💡 The fix: Force the 'location' property to be the MongoDB ID
    location: areaId, 
    date,
    isPublished: draggedSchedule.isPublished ?? true,
    originalScheduleId: draggedSchedule.originalScheduleId || draggedSchedule.id.split("-")[0],
    areaId: areaId, // areaId is still passed for local state updates
    break: parseInt(draggedSchedule.breakDuration) || 0,
  };

  // ✅ Update the local state first for a responsive UI
  setSchedules(prev => {
    const updated = [...prev];
    const newDraggedSchedule = {
      ...draggedSchedule,
      date,
      location: locationName, // Use the friendly name for the local UI
      locationId: areaId,
      areaId,
    };
    updated.splice(draggedIndex, 1);
    updated.splice(destination.index, 0, newDraggedSchedule);
    return updated;
  });


  try {
    handleSaveSchedule([scheduleToSave]);
  } catch (err) {
    console.error("Error saving schedule to backend:", err);
  }
};



const ScheduleCard = ({ schedule, onEdit, onCopy, isAdmin, allSchedules }) => {
  const [showPopover, setShowPopover] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState('right'); 
  const cardRef = useRef(null); 

  const isCompleted = () => {
    const now = new Date();
    const start = new Date(`${schedule.date}T${schedule.start}:00`);
    let end = new Date(`${schedule.date}T${schedule.end}:00`);

    if (end <= start) {
      end.setDate(end.getDate() + 1);
    }

    return now > end;
  };

  const canEdit = isAdmin || schedule.isPublished;

  const weekSchedules = (allSchedules ?? []).filter((s) => {
    if (s.careWorker !== schedule.careWorker) return false;

    const current = new Date(schedule.date);
    const monday = new Date(current);
    const day = current.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    const daysToSubtract = day === 0 ? 6 : day - 1; // Sunday -> prev Monday
    monday.setDate(current.getDate() - daysToSubtract);

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    const schedDate = new Date(s.date);
    return schedDate >= monday && schedDate <= sunday;
  });

  // Dynamically adjust popover position based on viewport space
  useEffect(() => {
    if (showPopover && cardRef.current) {
      const cardRect = cardRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const popoverWidth = 320; // Matches w-80 (80 * 4px = 320px)

      // Check if there's enough space on the right, otherwise show on left
      if (cardRect.right + popoverWidth + 12 > viewportWidth) {
        setPopoverPosition('left'); // Place popover to the left of the card
      } else {
        setPopoverPosition('right'); // Default: place to the right
      }
    }
  }, [showPopover]);

  const isOpenSchedule = !schedule.careWorker;

  return (
    <div
      ref={cardRef}
      className={`relative group px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl text-xs mb-2 transition-all duration-300 shadow-md hover:shadow-2xl w-full overflow-hidden
        ${
          isOpenSchedule
            ? 'bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 hover:from-amber-100 hover:via-orange-100 hover:to-amber-200 border-2 border-dashed border-amber-400 hover:border-amber-500 min-h-[56px] max-h-[68px] animate-pulse-subtle'
            : isCompleted()
            ? 'bg-gradient-to-br from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 border-2 border-green-400 min-h-[40px] max-h-[52px]'
            : schedule.isPublished
            ? 'bg-gradient-to-br from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 border-2 border-green-400 min-h-[40px] max-h-[52px]'
            : 'bg-gradient-to-br from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 border-2 border-gray-400 min-h-[40px] max-h-[52px]'
        }
        ${canEdit ? 'cursor-pointer hover:scale-105' : 'cursor-default'}
      `}
      onClick={() => canEdit && onEdit(schedule)}
      onMouseEnter={() => setShowPopover(true)}
      onMouseLeave={() => setShowPopover(false)}
    >
      {isOpenSchedule && (
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-amber-400/10 via-orange-400/10 to-amber-400/10 animate-shimmer pointer-events-none" />
      )}

      <div className="relative z-10">
        <div className="flex items-center gap-2 font-bold text-gray-800 text-xs">
          <FiClock className={`${isOpenSchedule ? 'text-amber-600' : 'text-blue-600'}`} size={14} />
          <span>{formatTime(schedule.start)} - {formatTime(schedule.end)}</span>
        </div>

        {isOpenSchedule ? (
          <div className="mt-1.5 flex items-center gap-2">
            <div className="flex items-center gap-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-2.5 py-1 rounded-full shadow-sm">
              <svg className="w-3 h-3 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
              </svg>
              <span className="text-[10px] font-bold uppercase tracking-wide">Open Shift</span>
            </div>
            <span className="text-[9px] text-amber-700 font-medium italic">Click to assign worker</span>
          </div>
        ) : (
          schedule.careWorker && (
            <div className="mt-1 flex items-center gap-2">
              <span className="truncate font-medium text-gray-700 text-xs">{schedule.careWorker}</span>

              {schedule.description && (
                <div className="relative ml-2 flex-shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowNote((prev) => !prev);
                    }}
                    className="text-gray-600 cursor-pointer focus:outline-none"
                  >
                    <img
                      src="/edit_pencil_icon.svg"
                      alt="description"
                      className="w-3 h-3"
                    />
                  </button>

                  {showNote && (
                    <div className="absolute right-0 bottom-full mb-2 w-40 bg-gray-400 text-black text-xs p-2 rounded shadow-lg z-50">
                      {schedule.description}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        )}
      </div>

      {canEdit && (
        <div className="absolute top-2 right-2 hidden group-hover:flex gap-2 z-10">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(schedule);
            }}
            title="Edit"
            className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:shadow-lg hover:scale-105 transform transition-all duration-200 flex items-center gap-1"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit
          </button>

          {isAdmin && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onCopy(schedule);
              }}
              title="Copy"
              className="bg-gradient-to-r from-green-500 to-green-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:shadow-lg hover:scale-105 transform transition-all duration-200 flex items-center gap-1"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy
            </button>
          )}
        </div>
      )}

      {isAdmin && showPopover && (
        <div
          className={`absolute top-0 w-72 sm:w-80 bg-gradient-to-br from-white to-blue-50 border-2 border-blue-200 rounded-2xl shadow-2xl p-3 z-[100] text-xs backdrop-blur-sm ${
            popoverPosition === 'right'
              ? 'left-full ml-2'
              : 'right-full mr-2'
          }`}
          onMouseEnter={() => setShowPopover(true)}
          onMouseLeave={() => setShowPopover(false)}
        >
          <div className="mb-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg px-3 py-2 shadow-lg">
            <div className="flex items-center gap-2 mb-0.5">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <p className="font-bold text-sm">
                {schedule.careWorker.charAt(0).toUpperCase() + schedule.careWorker.slice(1)}
              </p>
            </div>
            <p className="text-blue-100 text-[10px] font-medium">Weekly Schedule Overview</p>
          </div>

          <ul className="space-y-1.5 max-h-60 overflow-y-auto pr-1 custom-scrollbar">
            {weekSchedules.length > 0 ? (
              [...weekSchedules]
                .sort((a, b) => new Date(a.date) - new Date(b.date))
                .map((ws, idx) => (
                  <li key={idx} className="bg-white rounded-lg p-2 shadow-sm border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all duration-200">
                    <div className="flex justify-between items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <FiCalendar className="text-blue-600 flex-shrink-0" size={12} />
                          <span className="font-semibold text-gray-800 text-[11px] truncate">{new Date(ws.date).toLocaleDateString("en-GB")}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-gray-600">
                          <FiMapPin className="text-green-600 flex-shrink-0" size={10} />
                          <span className="text-[10px] truncate">{ws.location}</span>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(ws);
                        }}
                        className="bg-gradient-to-r from-blue-500 to-blue-600 text-white text-[10px] px-2 py-1 rounded-md hover:shadow-md hover:scale-105 transform transition-all duration-200 font-medium flex-shrink-0"
                      >
                        View
                      </button>
                    </div>
                  </li>
                ))
            ) : (
              <li className="text-center py-8 text-gray-400">
                <FiCalendar className="mx-auto mb-2" size={32} />
                <p className="text-sm">No schedules this week</p>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const workerSchedules = useMemo(() => {

  if (!hoveredWorker) {
    // console.log("No hovered worker yet — returning empty array.");
    return [];
  }

  const filtered = schedules.filter(s => {
    const includes = s.careWorkers.includes(hoveredWorker);
    // console.log(`Checking schedule ${s.id}: careWorkers=${JSON.stringify(s.careWorkers)}, includes hovered?`, includes);
    return includes;
  });

  return filtered;
}, [hoveredWorker, schedules]);


  
  const avatarColors = [
  'bg-blue-500',
  'bg-green-500',
  'bg-pink-500',
  'bg-gray-500',
  'bg-indigo-500',
];



  const publishButtonText = pendingSchedules.length === 0 ? 'Published' : 'Publish';
  const hasUnpublishedShifts = pendingSchedules.length > 0;

  if (isLoading || isAuthLoading) {
    return (
      <div className="flex flex-col h-screen bg-gray-100 font-sans">
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center justify-center py-10">
  <svg
    className="animate-spin h-8 w-8 text-blue-600 mb-3"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    ></circle>
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.37 0 0 5.37 0 12h4zm2 5.29A7.96 7.96 0 014 12H0c0 3.04 1.13 5.82 3 7.94l3-2.65z"
    ></path>
  </svg>
  <p className="text-gray-700 text-base sm:text-lg font-medium">Please wait...</p>
</div>
        </div>
      </div>
    );
  }

  const filteredCareWorkers = careWorkers.filter(worker => worker.role !== 'admin').filter(worker=>worker.name.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 font-sans overflow-hidden">
      <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 sm:p-6 bg-white backdrop-blur-xl border-b border-gray-200/50 shadow-lg flex-shrink-0 z-[100]">
        <div className="flex items-center justify-between w-full sm:w-auto mb-2 sm:mb-0">
          <div className="flex items-center ">
            <button
              className="sm:hidden p-2 text-gray-600 hover:text-gray-800"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7"></path>
              </svg>
            </button>
            <div ref={locationDropdownRef} className="relative ">
              <button
                onClick={() => isAdmin && setIsHeaderLocationDropdownOpen(!isHeaderLocationDropdownOpen)}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl bg-gradient-to-r from-blue-50 to-purple-50 hover:from-blue-100 hover:to-purple-100 text-gray-800 text-xs sm:text-sm font-bold transition-all duration-200 min-h-[44px] border-2 border-blue-200/50 hover:border-blue-400 shadow-sm hover:shadow-md"
              >
                <span className="mr-0">
                  <img src="/location.svg" alt="" className="w-4 h-4 sm:w-5 sm:h-5" />
                </span>
                <span className="truncate font-bold">{headerLocationButtonText}</span>
                {isAdmin && (
                  <svg
                    className="ml-1 w-3 sm:w-4 h-3 sm:h-4 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                )}
              </button>
              {isHeaderLocationDropdownOpen && isAdmin && (
                <div className="fixed left-4 top-[100px] w-72 sm:w-80 rounded-2xl shadow-2xl bg-white/95 backdrop-blur-xl ring-2 ring-blue-300 z-[110] max-h-[500px] overflow-y-auto border border-gray-200">
                  <div className="p-3">
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-3 rounded-t-xl -mx-3 -mt-3 mb-3">
                      <h3 className="font-bold text-sm flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Select Locations
                      </h3>
                    </div>
                    <input
                    ref={headerLocationSearchInputRef}
                    type="text"
                    placeholder="🔍 Search locations..."
                    value={locationSearch}
                    onChange={(e) => setLocationSearch(e.target.value)}
                    className="mb-3 px-4 py-2.5 w-full border-2 border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                  />
                    <label className="flex items-center px-3 py-2.5 text-sm text-gray-800 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 cursor-pointer rounded-lg mb-2 border border-transparent hover:border-blue-300 transition-all">
                      <input
                        type="checkbox"
                        className="form-checkbox h-4 w-4 accent-blue-600 rounded"
                        checked={selectedLocations.length === allAvailableAreas.length}
                        onChange={handleSelectAllLocations}
                      />
                      <span className="ml-3 font-bold">
                        {selectedLocations.length === allAvailableAreas.length ? 'Deselect All' : 'Select All'}
                      </span>
                    </label>
                    <div className="border-t-2 border-gray-200 my-2"></div>
                    {allAvailableAreas.filter(loc => {
    const search = locationSearch.toLowerCase();
    const label = `${loc.workType ? `${loc.workType} - ` : ''}${loc.name}`.toLowerCase();
    return label.includes(search);
  }).length > 0 ? (
  allAvailableAreas.filter(loc => {
    const search = locationSearch.toLowerCase();
    const label = `${loc.workType ? `${loc.workType} - ` : ''}${loc.name}`.toLowerCase();
    return label.includes(search);
  }).map((loc) => (
                      <label
                        key={`loc-${loc._id}`}
                        className="flex items-center px-3 py-2.5 text-sm text-gray-800 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 cursor-pointer rounded-lg mb-1 border border-transparent hover:border-blue-300 transition-all"
                      >
                        <input
                          type="checkbox"
                          className="form-checkbox h-4 w-4 accent-blue-600 rounded"
                          value={loc._id}
                          checked={selectedLocations.includes(loc._id)}
                          onChange={() => handleLocationCheckboxChange(loc._id)}
                        />
                        <span className="ml-3 font-semibold flex items-center gap-2">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          </svg>
                          {loc.name}
                        </span>
                      </label>
                    ))):(
  <div className="px-3 sm:px-4 py-2 text-sm text-gray-500 italic">
    No locations found
  </div>
)}
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-600 rounded-2xl text-white text-sm font-bold shadow-xl shadow-blue-500/40 overflow-hidden ml-2 sm:ml-6 relative transform transition-transform hover:scale-105">
              <button
                onClick={handlePreviousWeek}
                className="px-4 sm:px-5 py-3 sm:py-3.5 hover:bg-white/30 active:bg-white/40 transition-all duration-200 backdrop-blur-xl min-h-[44px] sm:min-h-[48px] font-bold text-xl border-r border-white/30"
              >
                ‹
              </button>
              <div className="relative flex items-center">
                <button 
                  onClick={() => setShowDatePicker(!showDatePicker)}
                  className="px-4 sm:px-6 py-2 sm:py-2.5 truncate font-bold flex items-center gap-2 hover:bg-white/10 transition-all duration-200"
                >
                  <FiCalendar size={16} />
                  <span>{weekRangeLabel()}</span>
                </button>
                {showDatePicker && (
                  <div className="absolute top-full mt-3 left-0 z-50 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-blue-300/50 p-4 ring-1 ring-blue-500/20">
                    <input
                      type="date"
                      onChange={handleDateSelect}
                      className="px-4 py-3 border-2 border-blue-400/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 font-medium cursor-pointer bg-white shadow-sm"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                )}
              </div>
              <button
                onClick={handleNextWeek}
                className="px-4 sm:px-5 py-3 sm:py-3.5 hover:bg-white/30 active:bg-white/40 transition-all duration-200 backdrop-blur-xl min-h-[44px] sm:min-h-[48px] font-bold text-xl border-l border-white/30"
              >
                ›
              </button>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2 sm:space-x-4 w-full sm:w-auto">
          <div className="flex items-center space-x-2 sm:space-x-4 text-gray-600 text-xs sm:text-sm flex-wrap gap-y-2">
            <motion.img
        src="/manocore_ai_logo.png"
        alt="AI Logo"
        className="h-6 w-auto sm:h-8 object-contain cursor-pointer"
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ repeat: Infinity, duration: 1.5 }}
        onClick={() => setShowSearch(prev => !prev)}
      />
      <AnimatePresence>
        {showSearch && (
          <motion.div
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 12 }}
            transition={{ duration: 0.2 }}
            className="flex items-center border border-gray-300 rounded-full px-2 py-1 w-64 bg-white shadow-md"
          >
            {/* Plus Icon */}
            <button
              type="button"
              className="text-gray-600 hover:text-blue-600 mr-2"
            >
              <FiPlusCircle size={20} />
            </button>

            {/* Input Field */}
            <input
              type="text"
              placeholder="Type something..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className="flex-grow bg-transparent outline-none text-sm text-black placeholder-gray-500"
            />

            {/* Send Button */}
            <button
              type="button"
              className="ml-2 text-black hover:text-blue-600"
              onClick={() => {
                console.log("Send:", inputValue);
                setInputValue("");
              }}
            >
              <FiSend size={18} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>


            <button type="button" onClick={handleRefresh} className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 " title="Refresh">
              <img src="/refresh.svg" alt="" className="w-4 sm:w-[16px] hover:animate-spin" />
            </button>
            {/* {isAdmin && (
        <button
            type="button"
            onClick={handleRemindClick}
            disabled={isRemindLoading}
            className={`flex items-center gap-2 px-3 py-2 rounded-md transition-colors duration-200 text-white ${isRemindLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'}`}
        >
            <span className="text-sm font-bold">
                {isRemindLoading ? 'Sending...' : 'Remind All'}
            </span>
        </button>
    )} */}
            {isAdmin && <button
              type="button"
              onClick={handleExport}
              className="flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-gray-50 to-white border-2 border-gray-300 hover:border-blue-400 hover:from-blue-50 hover:to-white transition-all duration-300 shadow-md hover:shadow-xl transform hover:-translate-y-0.5 group"
            >
              <img src="/exporticon.svg" alt="Export" className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" title="Export"/>
              <span className="text-sm font-bold text-gray-800 group-hover:text-blue-700">Export</span>
            </button>}
            {isAdmin && <button
              type="button"
              onClick={handlePublishClick}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl hover:shadow-2xl transition-all duration-300 text-white font-bold border-2 transform hover:-translate-y-0.5 ${
                hasUnpublishedShifts
                  ? 'bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-600 hover:shadow-blue-500/50 border-blue-500 animate-pulse-subtle'
                  : 'bg-gradient-to-r from-green-600 via-green-700 to-emerald-600 hover:shadow-green-500/50 border-green-500'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm">{publishButtonText}</span>
            </button>}
            <button
              onClick={() => setShowKeyboardHelp(true)}
              className="p-2.5 bg-gradient-to-r from-purple-100 to-blue-100 hover:from-purple-200 hover:to-blue-200 text-purple-700 rounded-xl transition-all duration-300 shadow-md hover:shadow-lg border-2 border-purple-300 hover:border-purple-400"
              title="Keyboard Shortcuts (?)"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        <aside className={`flex flex-col fixed sm:static inset-y-0 left-0 w-56 sm:w-64 bg-gradient-to-b from-white to-gray-50 border-r border-gray-200/50 shadow-xl flex-shrink-0 transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full sm:translate-x-0'} z-10 h-full overflow-visible`}>
          <div className="p-3 sm:p-4 flex-shrink-0">
          
          <h2 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-3 sm:mb-4">Care Workers</h2>
          <div className="relative mb-3 sm:mb-4">
            <input
      type="text"
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
      placeholder="Search workers..."
      className="w-full pl-10 pr-10 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white shadow-sm transition-all"
    />
            <svg
              className="absolute left-2 top-1/2 -translate-y-1/2 w-3 sm:w-4 h-3 sm:h-4 text-gray-400"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                clipRule="evenodd"
              ></path>
            </svg>
            {searchQuery && (
    <button
      onClick={() => setSearchQuery("")}
      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
    >
      ✕
    </button>
  )}
          </div>
          </div>
          <div className="flex-1 overflow-y-auto px-3 sm:px-4 pb-4 relative">
          <div className="space-y-2">
           {filteredCareWorkers.sort((a, b) => a.name.localeCompare(b.name)).map((worker, index) => (
  <div
    key={worker._id}
    data-worker-id={worker._id}
    className="flex items-center p-2 bg-white hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 rounded-lg border border-gray-200 hover:border-blue-400 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer relative group"
    onMouseEnter={() => {setHoveredWorker(worker._id);}}
            onMouseLeave={() => setHoveredWorker(null)}
  >
{/* Avatar with fallback to initials */}
{worker.avatarStyle ? (
  <img
    src={worker.avatarStyle}
    alt={worker.name}
    className="w-9 h-9 rounded-full object-cover mr-2 ring-1 ring-blue-400/30 group-hover:ring-blue-500"
  />
) : (
  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center font-bold mr-2 text-xs ring-1 ring-blue-400/30 group-hover:ring-blue-500">
    {worker.name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()}
  </div>
)}



    <span className="text-gray-800 text-xs font-semibold truncate max-w-[50%] group-hover:text-blue-700">
      {worker.name}
    </span>
    <span className="ml-auto text-[10px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
      {calculateHoursWorked(worker.name)}h
    </span>
    {hoveredWorker === worker._id && (
        <div 
          className="absolute left-full top-0 ml-2 bg-white border-2 border-blue-400 shadow-2xl rounded-xl p-3 z-[200] w-72 max-h-[500px] overflow-y-auto pointer-events-auto"
        >
          <div className="absolute left-[-8px] top-3 w-4 h-4 bg-white border-l-2 border-t-2 border-blue-300 transform rotate-45"></div>
            <div className="text-sm mb-3 border-b-2 border-blue-200 pb-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg px-3 py-2 shadow-lg">
  <p className="text-xs opacity-90">Weekly Schedule</p>
  <p className="font-bold text-base mt-1">
    {worker.name}
  </p>
  <p className="text-xs opacity-90 mt-1">{calculateHoursWorked(worker.name)}h this week</p>
</div>

          {workerSchedules.length > 0 ? (
            <div className="space-y-2">
            {[...workerSchedules]
             .sort((a, b) => new Date(a.date) - new Date(b.date)).map(s => {
              const formattedDate = new Date(s.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
              const timeRange = `${s.startTime} - ${s.endTime}`;
              return (
              <div key={s.id} className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-2 border border-blue-200 hover:border-blue-400 hover:shadow-md transition-all duration-200">
  
  <div className="flex items-start justify-between gap-2">
    <div className="flex-1">
      <p className="text-xs font-bold text-gray-900">{formattedDate}</p>
      <p className="text-[10px] text-gray-600 mt-0.5">{s.location}</p>
      <p className="text-[10px] text-blue-600 font-semibold mt-0.5">{timeRange}</p>
    </div>
    <button
      onClick={() => {
        const scheduleId = `schedule-${s.id}`;
        const el = document.getElementById(scheduleId);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("ring-4", "ring-blue-400");
          setTimeout(() => {
            el.classList.remove("ring-4", "ring-blue-400");
          }, 1500);
        }
      }}
      className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white text-[10px] px-2 py-1 rounded-md shadow-sm hover:shadow-md transition-all duration-200 font-semibold"
    >
      View
    </button>
  </div>
</div>

            )})
            }
            </div>
          ) : (
            <div className="text-xs text-gray-500 text-center py-2">No schedules this week</div>
          )}
        </div>
      )}
  </div>
))}

          </div>
          </div>
        </aside>
<DragDropContext onDragEnd={handleDragEnd}>
        <main className="flex-1 flex flex-col bg-gradient-to-br from-blue-50/30 via-white to-purple-50/30 min-w-0 overflow-hidden">
          {error && (
            <div className="mx-4 mt-4 bg-gradient-to-r from-red-50 to-red-100 border-2 border-red-300 text-red-800 px-6 py-4 rounded-2xl text-sm sm:text-base flex items-center justify-center gap-3 shadow-xl backdrop-blur-sm">
              <svg className="w-6 h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="font-semibold">{error}</span>
            </div>
          )}
          <div className="sticky top-0 z-20 bg-white backdrop-blur-xl grid grid-cols-1 sm:grid-cols-7 gap-2 sm:gap-3 p-3 sm:p-4 mx-4 mt-4 mb-0 rounded-2xl shadow-xl border-2 border-blue-200/50 flex-shrink-0">{days.map((day) => {
              const isToday = day.dateStr === new Date().toISOString().split('T')[0];
              const currentHour = new Date().getHours();
              let timeIcon, timeGradient, iconAnimation;
              
              if (isToday) {
                if (currentHour >= 5 && currentHour < 8) {
                  timeIcon = <FiSunrise className="text-orange-400 animate-pulse" />;
                  timeGradient = 'from-orange-400 via-orange-500 to-pink-500';
                  iconAnimation = 'animate-bounce';
                } else if (currentHour >= 8 && currentHour < 17) {
                  timeIcon = <FiSun className="text-yellow-400 animate-spin-slow" />;
                  timeGradient = 'from-yellow-400 via-orange-400 to-yellow-500';
                  iconAnimation = 'animate-pulse';
                } else if (currentHour >= 17 && currentHour < 20) {
                  timeIcon = <FiSunset className="text-orange-500 animate-pulse" />;
                  timeGradient = 'from-orange-500 via-red-500 to-purple-600';
                  iconAnimation = 'animate-bounce';
                } else {
                  timeIcon = <FiMoon className="text-indigo-400 animate-pulse" />;
                  timeGradient = 'from-indigo-600 via-purple-600 to-indigo-800';
                  iconAnimation = 'animate-pulse';
                }
              }
              
              return (
                <motion.div
                  key={day.dateStr}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: days.indexOf(day) * 0.05 }}
                  className={`relative overflow-hidden flex flex-col items-center justify-center py-3 px-2 sm:px-4 rounded-xl transition-all duration-300 ${
                    isToday
                      ? 'bg-white text-gray-900 shadow-xl scale-105 border-2 border-blue-500'
                      : 'bg-white text-gray-800 hover:shadow-lg hover:scale-102 border border-gray-200'
                  } min-h-[70px] sm:min-h-[80px] cursor-pointer group`}
                >
                  <div className="relative z-10 flex flex-col items-center gap-1">
                    <div className="flex items-center gap-2">
                      {isToday && <div className={iconAnimation}>{timeIcon}</div>}
                      <span className={`text-xs font-bold uppercase tracking-wider ${
                        isToday ? 'text-gray-700' : 'text-gray-600 group-hover:text-blue-600'
                      }`}>{day.shortDay}</span>
                    </div>
                    <span className={`text-2xl sm:text-3xl font-extrabold ${
                      isToday ? 'text-gray-900' : 'text-gray-900 group-hover:text-blue-600'
                    }`}>{day.dayNum}</span>
                    {isToday && (
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-blue-600 mt-1">Today</span>
                    )}
                  </div>
                  {!isToday && (
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-600/0 to-blue-600/0 group-hover:from-blue-600/5 group-hover:to-blue-600/10 transition-all duration-300 rounded-xl" />
                  )}
                </motion.div>
              );
            })}
          </div>

          <div className="flex-1 overflow-y-auto px-4 pb-6">
          {currentVisibleLocations.length > 0 ? (
            <>
              {currentVisibleLocations.map((area) => (
                <div key={area._id} className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl mb-3 sm:mb-4 border-2 border-gray-200/50 overflow-hidden hover:shadow-2xl hover:border-blue-300/50 transition-all duration-300 mt-4">
                  <button
                    onClick={() => toggleLocationDropdown(area._id)}
                    className={`flex justify-between items-center w-full px-4 sm:px-6 py-3.5 sm:py-4 font-bold rounded-t-2xl transition-all duration-300 text-sm sm:text-base min-h-[56px] group ${
                      openLocationDropdowns.has(area._id)
                        ? 'bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-600 text-white shadow-lg shadow-blue-500/30'
                        : area._id === 'time-off'
                          ? 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 hover:from-gray-200 hover:to-gray-300'
                          : 'bg-gradient-to-r from-blue-100 to-purple-100 text-blue-900 hover:from-blue-200 hover:to-purple-200'
                    }`}
                  >
                    <span className="flex items-center gap-2 truncate">
                      <div className={`relative w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 ${
                        openLocationDropdowns.has(area._id)
                          ? 'bg-white/20 backdrop-blur-xl'
                          : area._id === 'time-off'
                            ? 'bg-gray-200'
                            : 'bg-blue-200'
                      }`}>
                        <FiMapPin className={`text-base ${
                          openLocationDropdowns.has(area._id)
                            ? 'text-white'
                            : area._id === 'time-off'
                              ? 'text-gray-700'
                              : 'text-blue-700'
                        }`} />
                      </div>
                      <span className="font-bold text-sm sm:text-base">
                        {area.workType ? `${area.workType} - ` : ''}{area.name}
                      </span>
                    </span>
                    <svg
                      className={`h-4 w-4 transform transition-all duration-300 ${
                        openLocationDropdowns.has(area._id) ? 'rotate-180 text-white' : 'text-gray-500 group-hover:text-blue-600'
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </button>
                  {openLocationDropdowns.has(area._id) && (
                    <div key={`dropdown-${area._id}`} className="bg-gradient-to-br from-gray-50 to-white rounded-b-xl shadow-inner">
                      <div className="grid grid-cols-[repeat(7,minmax(0,1fr))] gap-2 sm:gap-3 py-3 sm:py-4 px-2 sm:px-3">
                        {days.map((day) => (
                          <div
                            key={day.dateStr}
                            className="w-full bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 p-2 sm:p-3 flex flex-col justify-between min-h-[110px] sm:min-h-[130px] border border-gray-100 hover:border-blue-200"
                          >
                            <div className="flex-grow">
<div className="flex flex-col justify-between h-full">
  <Droppable droppableId={`${area._id}__${day.dateStr}`}>
    {(provided) => (
      <div
        ref={provided.innerRef}
        {...provided.droppableProps}
        className="flex flex-col gap-2 flex-grow"
      >
        {getSortedDaySchedules(day.dateStr, area.name, area._id).map((schedule, index) => {
          if (isAdmin) {
    return (
          <Draggable draggableId={schedule.id} index={index} key={schedule.id}>
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                id={`schedule-${schedule.id}`} 
              >
                {/* {console.log("Rendering schedule with id:", `schedule-${schedule.id}`)} */}
                <ScheduleCard
                  schedule={schedule}
                  onEdit={handleEditSchedule}
                  onCopy={handleCopy}
                  isAdmin={isAdmin}
                  allSchedules={schedules}
                />
              </div>
            )}
         
          </Draggable>)}else {
             return (
    <div key={schedule.id} id={`schedule-${schedule.id}`}>
      {console.log("Rendering schedule with id:", `schedule-${schedule.id}`)}
      <ScheduleCard
        schedule={schedule}
        onEdit={handleEditSchedule}
        onCopy={handleCopy}
        isAdmin={isAdmin}
      />
    </div>
  );
}
})}
        {provided.placeholder}
      </div>
    )}
  </Droppable>

  {isAdmin && (
    <button
      onClick={() =>
        copiedSchedule
          ? handlePaste(day.dateStr, area.name, area._id)
          : handleAddSchedule(day.dateStr, area.name)
      }
      className={`relative group mt-2 mx-auto w-8 h-8 rounded-full transition-all duration-300 flex items-center justify-center shadow-sm hover:shadow-lg transform hover:scale-110 ${
        copiedSchedule
          ? 'bg-gradient-to-br from-purple-500 to-purple-600 text-white hover:shadow-purple-500/30'
          : 'bg-gradient-to-br from-blue-500 to-blue-600 text-white hover:shadow-blue-500/30'
      }`}
      title={copiedSchedule ? 'Paste copied schedule' : 'Add new schedule'}
    >
      {copiedSchedule ? (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          viewBox="0 0 24 24"
        >
          <path d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-2 10h2a2 2 0 002-2v-8a2 2 0 00-2-2h-2m-4 12V8a2 2 0 012-2h4" />
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
      )}
    </button>
  )}
</div>


                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {isAdmin && (
                <div className="flex justify-center mt-4 sm:mt-6">
                  <button
                    onClick={() => setIsNewAreaModalOpen(true)}
                    className="bg-blue-400 text-white rounded-md py-2 sm:py-3 px-4 sm:px-6 font-medium hover:bg-blue-500 transition-colors duration-200 text-xs sm:text-sm min-h-[44px]"
                  >
                    + Add New Area
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 sm:py-10 text-gray-500 text-xs sm:text-sm">
              No locations available
            </div>
          )}
          </div>
        </main>
</DragDropContext>
      </div>
      {dropErrorModal.open && (
  <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[500]" onClick={() => setDropErrorModal({ open: false, message: '' })}>
    <div className="bg-white rounded-lg p-6 w-11/12 max-w-sm shadow-lg" onClick={(e) => e.stopPropagation()}>
      <h2 className="text-lg font-semibold mb-3">Schedule Conflict</h2>
      <p className="text-sm text-gray-700 mb-4">{dropErrorModal.message}</p>
      <div className="flex justify-center">
        <button
          onClick={() => setDropErrorModal({ open: false, message: '' })}
          className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm"
        >
          OK
        </button>
      </div>
    </div>
  </div>
)}


      <AssignmentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        assignment={selectedSchedule}
        dayDate={modalDayDate}
        availableLocations={allAvailableAreas}
        defaultLocation={modalLocation}
        availableCareWorkers={careWorkers}
        onSave={handleSaveSchedule}
        onDelete={handleDeleteSchedule}
        schedules={schedules}
        pendingSchedules={pendingSchedules}
        isAdmin={isAdmin}
      />

      <NewAreaModal
        isOpen={isNewAreaModalOpen}
        onClose={() => setIsNewAreaModalOpen(false)}
        availableCareWorkers={careWorkers}
        setLocations={setLocations}
      />

      <ConfirmDeleteModal
        isOpen={isConfirmDeleteModalOpen}
        onClose={() => {
          setIsConfirmDeleteModalOpen(false);
          setScheduleToDelete(null);
        }}
        onConfirm={confirmDeleteSchedule}
      />

      <ConfirmPublishModal
        isOpen={isConfirmPublishModalOpen}
        onClose={() => setIsConfirmPublishModalOpen(false)}
        onConfirm={handlePublish}
      />

      {/* Publish Consent Modal with Notification Options */}
      {isPublishConsentOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 backdrop-blur-xl p-3 rounded-xl">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Publish Schedules</h2>
                  <p className="text-blue-100 text-sm mt-1">Send notifications to team members</p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              <p className="text-gray-700 mb-4">
                You're about to publish <span className="font-bold text-blue-600">{pendingSchedules.length} schedule(s)</span>. 
                Choose how you'd like to notify your team:
              </p>

              {/* Notification Options */}
              <div className="space-y-3">
                <label className="flex items-center gap-3 p-3 rounded-xl border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={notificationPreferences.email}
                    onChange={(e) => setNotificationPreferences(prev => ({ ...prev, email: e.target.checked }))}
                    className="w-5 h-5 accent-blue-600 rounded"
                  />
                  <div className="flex items-center gap-2 flex-1">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span className="font-medium text-gray-800 group-hover:text-blue-700">Email Notification</span>
                  </div>
                </label>

                <label className="flex items-center gap-3 p-3 rounded-xl border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={notificationPreferences.mobileNotification}
                    onChange={(e) => setNotificationPreferences(prev => ({ ...prev, mobileNotification: e.target.checked }))}
                    className="w-5 h-5 accent-blue-600 rounded"
                  />
                  <div className="flex items-center gap-2 flex-1">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    <span className="font-medium text-gray-800 group-hover:text-blue-700">Push Notification</span>
                  </div>
                </label>

                <label className="flex items-center gap-3 p-3 rounded-xl border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={notificationPreferences.sms}
                    onChange={(e) => setNotificationPreferences(prev => ({ ...prev, sms: e.target.checked }))}
                    className="w-5 h-5 accent-blue-600 rounded"
                  />
                  <div className="flex items-center gap-2 flex-1">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                    <span className="font-medium text-gray-800 group-hover:text-blue-700">SMS</span>
                  </div>
                </label>

                <label className="flex items-center gap-3 p-3 rounded-xl border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={notificationPreferences.whatsapp}
                    onChange={(e) => setNotificationPreferences(prev => ({ ...prev, whatsapp: e.target.checked }))}
                    className="w-5 h-5 accent-blue-600 rounded"
                  />
                  <div className="flex items-center gap-2 flex-1">
                    <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    <span className="font-medium text-gray-800 group-hover:text-blue-700">WhatsApp</span>
                  </div>
                </label>
              </div>

              <p className="text-xs text-gray-500 mt-4">
                ℹ️ All notification options are optional. You can publish without sending notifications.
              </p>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex gap-3">
              <button
                onClick={() => {
                  setIsPublishConsentOpen(false);
                  setNotificationPreferences({ email: false, mobileNotification: false, sms: false, whatsapp: false });
                }}
                className="flex-1 px-4 py-3 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition-all font-semibold text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setIsPublishConsentOpen(false);
                  handlePublish();
                }}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/30 transition-all font-bold"
              >
                Publish Now
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Keyboard Shortcuts Help Modal */}
      {showKeyboardHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 backdrop-blur-xl p-3 rounded-xl">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Keyboard Shortcuts</h2>
                    <p className="text-purple-100 text-sm mt-1">Work faster with these shortcuts</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowKeyboardHelp(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* General Actions */}
                <div className="space-y-3">
                  <h3 className="font-bold text-gray-900 text-lg mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                    General
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700">New Schedule</span>
                      <kbd className="px-3 py-1 bg-white border border-gray-300 rounded-md text-xs font-mono shadow-sm">Ctrl + N</kbd>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700">Save/Publish</span>
                      <kbd className="px-3 py-1 bg-white border border-gray-300 rounded-md text-xs font-mono shadow-sm">Ctrl + S</kbd>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700">Search</span>
                      <kbd className="px-3 py-1 bg-white border border-gray-300 rounded-md text-xs font-mono shadow-sm">Ctrl + F</kbd>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700">Print/Export</span>
                      <kbd className="px-3 py-1 bg-white border border-gray-300 rounded-md text-xs font-mono shadow-sm">Ctrl + P</kbd>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700">Close Modal</span>
                      <kbd className="px-3 py-1 bg-white border border-gray-300 rounded-md text-xs font-mono shadow-sm">ESC</kbd>
                    </div>
                  </div>
                </div>

                {/* Navigation */}
                <div className="space-y-3">
                  <h3 className="font-bold text-gray-900 text-lg mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                    Navigation
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700">Previous Week</span>
                      <kbd className="px-3 py-1 bg-white border border-gray-300 rounded-md text-xs font-mono shadow-sm">← Left Arrow</kbd>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700">Next Week</span>
                      <kbd className="px-3 py-1 bg-white border border-gray-300 rounded-md text-xs font-mono shadow-sm">→ Right Arrow</kbd>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700">Go to Today</span>
                      <kbd className="px-3 py-1 bg-white border border-gray-300 rounded-md text-xs font-mono shadow-sm">T</kbd>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700">Refresh</span>
                      <kbd className="px-3 py-1 bg-white border border-gray-300 rounded-md text-xs font-mono shadow-sm">R</kbd>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700">Help</span>
                      <kbd className="px-3 py-1 bg-white border border-gray-300 rounded-md text-xs font-mono shadow-sm">?</kbd>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-200">
                <p className="text-sm text-gray-700">
                  <strong>💡 Pro Tip:</strong> Use <kbd className="px-2 py-1 bg-white border border-gray-300 rounded text-xs font-mono">Cmd</kbd> instead of <kbd className="px-2 py-1 bg-white border border-gray-300 rounded text-xs font-mono">Ctrl</kbd> on Mac
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4">
              <button
                onClick={() => setShowKeyboardHelp(false)}
                className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:shadow-lg transition-all font-bold"
              >
                Got it!
              </button>
            </div>
          </motion.div>
        </div>
      )}


    </div>
  );
};

export default SchedulePage;

